/**
 * LiveStroke — the real-time tracer behind the Mark tool.
 *
 * The stroke is read as a sequence of stretches. In each, the finger is doing
 * one of three things:
 *
 *   mid   running between two solid parallel lines (a duct bank, a trench):
 *         the band's sides ARE those two lines — the plan's own rails — and
 *         its ends are cut square across them;
 *   snap  following one line: the band is a constant-width band on that rail;
 *   edge  a corridor that lost one edge (a bank merging into a wider trench) while
 *         the finger keeps to the bank's own width: the surviving line is one side
 *         and the other runs at the old width, until the finger moves out into the
 *         trench (then the trench's corridor takes over);
 *   free  nothing to follow: the finger's own route, de-jittered.
 *
 * Rails (src/vision/rails.ts) are fixed geometry, so a rail stretch's sides are
 * exact and never move; the finger sets one thing about them — how far they
 * reach. The band's far end IS the finger: it runs on as the finger goes on and
 * comes back as the finger comes back (a stretch the finger backs out of
 * altogether is undone, and the one before it goes on from the finger). The
 * finger is used for one other decision only: which rails bracket it. That
 * decision is made over a short window behind the finger and re-checked every
 * frame; a stretch ends when the finger leaves its rails or a rail ends, and a
 * new stretch begins there. While the finger is coming back inside a stretch it
 * has already read further, nothing is re-read.
 */
import { nearestOnSegment, polylineLength, simplify, chaikin } from '../editor/geom.ts';
import type { Vec } from '../../shared/doc.ts';
import type { IntentEngine } from './engine.ts';
import { joinSides, offsetSide, strokePolygon, unionPolygons } from '../editor/outline.ts';
import { projectOnRail, railPieceAt, railPointAt, railSlice, railsCross, type Box, type Projection, type Rail } from './rails.ts';
import { edgeOffset, refineSide, type Luma } from './ink.ts';

export type LiveMode = 'snap' | 'free' | 'mid' | 'edge';

export interface LiveResult {
  path: number[]; // midline, image px
  outline: number[]; // closed polygon: the band's border as it will be committed (all stretches joined)
  polys: number[][]; // closed polygons to draw: consecutive rail stretches joined into one, each free stretch its own (unioned on commit)
  mode: LiveMode;
  width: number | null; // band width of the corridor being followed (or the widest so far)
  version: number; // changes whenever the reading's structure changes (a stretch appears, ends, is replaced)
  thinking: boolean; // nothing to show yet: the plan is still being read under the finger
}

export interface LiveBand {
  pts: number[]; // closed polygon (the border), image px
  path: number[]; // cleaned midline
  width: number;
  label: string;
}

export interface LiveOptions {
  width: number; // band width for lines and free strokes (image px)
  margin: number; // how far outside a corridor's edge lines (their centres) the band's border sits (image px); 0 = on the lines
}

/** One rail used as a side of a band. */
interface Side {
  rail: Rail;
  fwd: boolean; // rail direction agrees with the finger's travel
  s0: number; // arc length where the stretch starts on this rail
  sEnd: number; // arc length reached: where the finger is now along the rail (the band's far end)
  far: number; // the furthest the reach has ever been along travel (the finger behind it is scrubbing back)
  off: number; // outward offset of the border from the rail's centre
  onLine: boolean; // the border sits on this rail's line (read from the ink); false for a side at a set width from it
}

interface Geometry {
  left: number[];
  right: number[];
  mid: number[];
}

interface Stretch {
  id: number;
  mode: LiveMode;
  from: number; // anchor index where it begins
  width: number; // band width (rails: rail to rail plus offsets; free: inherited or default)
  a: Side | null; // left rail, or the line itself (snap)
  b: Side | null; // right rail, or the line itself (snap)
  end: number; // last anchor at which the finger was inside (rails)
  checked: number; // last anchor examined (rails)
  misses: number;
  outSince: number; // rails: first anchor at which the finger left the corridor sideways (-1: inside)
  outTravel: number; // rails: how far the finger has travelled since leaving while not coming back (image px)
  outMax: number; // rails: farthest the finger has been out on this excursion
  divergeSince: number; // rails: first anchor at which the rails began to part (-1: running together)
  trench: number; // edge: the width of the wider corridor the finger could move out into (Infinity: none read)
  slack: number; // snap: how far off the line the finger may run and still be on it (image px)
  waitFrom: number; // snap, a bold line: the anchor from which its band has waited past its rail's end (-1: not waiting)
  trenchVotes: number; // edge: consecutive readings with the finger nearer the trench's centre than the bank's
  head: number[]; // free: points locked behind the finger
  lockRaw: number; // free: raw index the live part starts at
  final: Geometry | null; // set once the stretch is closed
  finalPoly?: number[]; // free: the stroked polygon of the closed stretch
}

/** A rail as seen from the finger over the evidence window. */
interface Eval {
  rail: Rail;
  side: 'L' | 'R' | 'O'; // left of travel, right, or under the finger
  d: number; // median distance (image px)
  dFirst: number; // distance at the first and last supporting anchors
  dLast: number;
  j: number; // anchor from which the rail supports the finger continuously
  fwd: boolean;
  ok: Uint8Array; // per window anchor: the rail runs alongside there
  tx: Float32Array; // rail tangent at each supporting anchor's projection
  ty: Float32Array;
}

/** A corridor that lost one edge: the line that goes on, which side of it the band lay, and the band's width. */
interface Ended {
  rail: Rail;
  bandSide: 'L' | 'R';
  width: number;
  thick: number;
  anchor: number;
  by: number; // the stretch whose close recorded it (undone with that stretch)
}

interface Found {
  mode: 'mid' | 'snap' | 'edge';
  a: Side;
  b: Side;
  j: number;
  width: number;
  dist?: number; // edge: the finger's distance from the line
  trench?: number; // edge: the width of the wider corridor around, when one is read
  slack?: number; // snap: how far off the line the finger may run and still be on it (a bold line: further)
}

const PARALLEL_COS = 0.7; // a rail within 45° of the finger's direction runs alongside it
const VEER_COS = Math.cos((15 * Math.PI) / 180); // a followed line turning off its own way by more than this …
const KEPT_COS = Math.cos((7 * Math.PI) / 180); // … while the finger keeps to that way has left it
const BOLD = 1.8;
const BOLD_GAP = 3; // a bold line is waited for across this many finger-widths of lost dashes // a line this many times heavier than another is a different kind of line (a trench, not its grading)

export class LiveStroke {
  readonly engine: IntentEngine;
  readonly tol: number; // image px
  readonly width: number;
  readonly margin: number;
  #raw: number[] = [];
  #ax: number[] = []; // anchors: finger samples about tol/3 apart (never move once added)
  #ay: number[] = [];
  #araw: number[] = []; // raw index of each anchor
  #step: number;
  #window: number; // anchors of evidence looked at behind the finger
  #minEvidence: number; // anchors a rail must support before it is followed
  #cap: number; // farthest a side line may be from the finger (image px)
  #widthCap: number; // widest corridor (image px)
  #closed: Stretch[] = [];
  #open: Stretch | null = null;
  #seen = 0; // anchors present at the last evaluation
  #lastMid: { a: Rail; b: Rail; thick: number; anchor: number; by: number } | null = null; // the corridor most recently followed
  #lastLine: { rail: Rail; thick: number; anchor: number; by: number } | null = null; // the line most recently followed (snap)
  #lastEnded: Ended | null = null; // a corridor that lost one edge: the line that goes on, and which side of it the band was
  #refused: { key: string; until: number } | null = null; // a reading validation threw out at once: not read again from the same evidence
  #region: Box = { minX: 0, minY: 0, maxX: 0, maxY: 0 }; // where crossings between rails are looked for (the window and its surroundings)
  #nextId = 1;
  #version = 0;
  #structure = '';
  lastFrameMs = 0;
  lastDebug: unknown = null;
  lastClose: string | null = null;
  readonly closes: string[] = []; // every close reason so far (diagnostics)

  constructor(engine: IntentEngine, tol: number, opts: Partial<LiveOptions> = {}) {
    this.engine = engine;
    this.tol = tol;
    this.width = opts.width ?? 2 * tol;
    this.margin = opts.margin ?? Math.max(2, 0.15 * tol);
    this.#step = Math.max(3, tol / 3);
    this.#window = Math.ceil((8 * tol) / this.#step);
    this.#minEvidence = Math.ceil((2 * tol) / this.#step);
    const L = Math.max(engine.plan.w, engine.plan.h);
    this.#cap = 0.05 * L;
    this.#widthCap = 0.065 * L;
  }

  get raw(): number[] {
    return this.#raw;
  }

  /** How far from a line the finger may run and still be following it (the finger's own tolerance). */
  get #lineReach(): number {
    return Math.max(2, 0.9 * this.tol);
  }
  /** For harnesses: every stretch with the rails it follows (full rail polylines). */
  get debugStretches(): { mode: LiveMode; width: number; trench: number; a: number[] | null; b: number[] | null; thick: number[]; start: Vec }[] {
    const all = this.#open ? [...this.#closed, this.#open] : this.#closed;
    return all.map((st) => ({ mode: st.mode, width: st.width, trench: st.trench, a: st.a?.rail.pts ?? null, b: st.b?.rail.pts ?? null, thick: [st.a?.rail.thick ?? 0, st.b?.rail.thick ?? 0], start: { x: this.#ax[st.from]!, y: this.#ay[st.from]! } }));
  }
  get mode(): LiveMode {
    return this.#open?.mode ?? 'free';
  }
  /** For harnesses: the corridor edge last lost, if any. */
  get debugEnded(): { bandSide: 'L' | 'R'; width: number; anchor: number } | null {
    return this.#lastEnded ? { bandSide: this.#lastEnded.bandSide, width: this.#lastEnded.width, anchor: this.#lastEnded.anchor } : null;
  }
  /** For harnesses: how the rail under a point reads against the current window, anchor by anchor. */
  debugRailAt(p: Vec): unknown {
    const hit = this.engine.nearest(p, 8);
    if (!hit) return null;
    const rail = this.engine.rails.railOf(hit.seg.id);
    const hi = this.#ax.length,
      lo = Math.max(0, hi - this.#window);
    const per: string[] = [];
    for (let i = lo; i < hi; i++) {
      const q = { x: this.#ax[i]!, y: this.#ay[i]! };
      const pr = projectOnRail(rail, q, this.#cap + 2);
      if (!pr) per.push('none');
      else if (pr.d > this.#cap) per.push('far' + Math.round(pr.d));
      else if (pr.over > 2) per.push('over');
      else {
        const t = this.#tangent(i);
        const dot = Math.abs(t.x * pr.tx + t.y * pr.ty);
        per.push(dot < PARALLEL_COS ? `angle${Math.round((Math.acos(dot) * 180) / Math.PI)}(f${Math.round((Math.atan2(t.y, t.x) * 180) / Math.PI)}/r${Math.round((Math.atan2(pr.ty, pr.tx) * 180) / Math.PI)}@${Math.round(pr.x)},${Math.round(pr.y)})` : 'ok' + Math.round(pr.d));
      }
    }
    const e = this.#evalRail(rail, lo, hi);
    return { rail: `#${rail.id}:${Math.round(rail.pts[0]!)}→${Math.round(rail.pts[rail.pts.length - 2]!)} t${rail.thick.toFixed(1)}`, eval: e ? { side: e.side, d: +e.d.toFixed(1), j: e.j - lo } : null, per: per.join(' ') };
  }
  /** Number of stretches the stroke has been read as so far. */
  get stretches(): number {
    return this.#closed.length + (this.#open ? 1 : 0);
  }
  /**
   * The band's sides as shown right now, one entry per run of consecutive rail stretches (their
   * sides joined as one line each) — a free stretch ends a run. For pulling a band's end along
   * the plan: the first run is the band's own lines and whatever they lead into, ending where
   * the finger is.
   */
  sideGroups(): { left: number[]; right: number[] }[] {
    return this.#groups(this.#assemble(false), false).filter((g) => !g.free).map((g) => ({ left: g.left, right: g.right }));
  }
  /** True while the finger's latest stretch has no reading yet (nothing to show but the finger). */
  get thinking(): boolean {
    const open = this.#open;
    if (!open) return true;
    if (open.mode !== 'free') return false;
    const parts = this.#assemble(false);
    return !parts.some((p) => p.st === open);
  }

  /** Add finger samples (image px). */
  add(points: Vec[]): void {
    for (const p of points) {
      const n = this.#raw.length;
      if (n && Math.hypot(p.x - this.#raw[n - 2]!, p.y - this.#raw[n - 1]!) < 0.5) continue;
      this.#raw.push(p.x, p.y);
      const m = this.#ax.length;
      if (m === 0 || Math.hypot(p.x - this.#ax[m - 1]!, p.y - this.#ay[m - 1]!) >= this.#step) {
        this.#ax.push(p.x);
        this.#ay.push(p.y);
        this.#araw.push(n);
      }
    }
  }

  /** Re-read the stroke for the current finger position. Call once per frame. */
  update(): LiveResult {
    const t0 = performance.now();
    const n = this.#ax.length;
    if (n >= 2) {
      if (!this.#open) this.#open = this.#freeStretch(0, this.width, []);
      // one anchor at a time, so the reading depends on the finger's route alone, never on
      // how many samples a frame happened to bring
      for (let h = Math.max(2, this.#seen + 1); h <= n; h++) this.#advance(h);
      this.#seen = n;
    }
    const r = this.result();
    this.lastFrameMs = performance.now() - t0;
    return r;
  }

  result(): LiveResult {
    const left: number[] = [],
      right: number[] = [],
      mid: number[] = [];
    const open = this.#open;
    const parts = this.#assemble(false);
    for (const { g } of parts) appendGeometry(g, left, right, mid);
    const polys = this.#polys(parts, false);
    // the reading's structure: which stretches are shown, on which lines
    const key = parts.map(({ st }) => `${st.id}${st.mode}${st.a?.rail.id ?? ''}:${st.b?.rail.id ?? ''}`).join(',');
    if (key !== this.#structure) {
      this.#structure = key;
      this.#version++;
    }
    let width: number | null = open && isCorridor(open.mode) ? open.width : null;
    if (width === null) for (const st of this.#closed) if (isCorridor(st.mode)) width = Math.max(width ?? 0, st.width);
    return { path: mid, outline: joinSides(left, right), polys, mode: open?.mode ?? 'free', width, version: this.#version, thinking: !parts.some((p) => p.st === open) && !!open && open.mode === 'free' };
  }

  /**
   * The border polygons to draw. Stretches read from the plan that follow one another
   * are one band: their sides run on from one to the next (across a widening, a hidden
   * stub, a symbol), so the band never shows a seam while it is drawn. A free stretch is
   * its own stroked shape.
   */
  #polys(parts: { st: Stretch; g: Geometry }[], final: boolean): number[][] {
    return this.#groups(parts, final).map((g) => g.free ?? joinSides(g.left, g.right));
  }

  /** The sides behind #polys: each run of rail stretches as one left and one right line, each free stretch as its own polygon. */
  #groups(parts: { st: Stretch; g: Geometry }[], final: boolean): { left: number[]; right: number[]; free: number[] | null }[] {
    const out: { left: number[]; right: number[]; free: number[] | null }[] = [];
    let left: number[] = [],
      right: number[] = [];
    let thick = Infinity,
      leftOn = true,
      rightOn = true,
      any = false;
    const luma = final && this.margin === 0 ? this.engine.luma : null;
    // A run of stretches along one line (or a line and the rail grown from another piece of it) is
    // one band on that line: their centres joined, the skeleton's jogs where other lines cross it
    // (smaller than the line's own weight) taken out, and the sides laid at the band's width.
    let runMid: number[] = [],
      runW = 0,
      runT = Infinity;
    const flushRun = () => {
      if (runMid.length >= 4) {
        const c = simplify(runMid, Math.max(0.6, 0.4 * runT));
        joinForward(left, offsetSide(c, runW / 2));
        joinForward(right, offsetSide(c, -runW / 2));
      }
      runMid = [];
      runW = 0;
      runT = Infinity;
    };
    const flush = () => {
      flushRun();
      if (left.length >= 4 && right.length >= 4) {
        if (luma && any && Number.isFinite(thick)) {
          // the finished sides once more against the ink, as a whole: the seams between
          // stretches come out on the line like everything else (a side that ran at a set
          // width from a line, not on one, is left as it is)
          if (leftOn) left = refineSide(luma, left, 1, thick, 0, 'position');
          if (rightOn) right = refineSide(luma, right, -1, thick, 0, 'position');
        }
        out.push({ left, right, free: null });
      }
      left = [];
      right = [];
      thick = Infinity;
      leftOn = rightOn = true;
      any = false;
    };
    for (const { st, g } of parts) {
      if (st.mode === 'free') {
        flush();
        out.push({ left: [], right: [], free: this.#polygon(st, g, final) });
        continue;
      }
      any = true;
      if (st.mode === 'snap') {
        joinForward(runMid, g.mid);
        runW = Math.max(runW, st.width);
        runT = Math.min(runT, st.a!.rail.thick);
        leftOn = rightOn = false;
        continue;
      }
      flushRun();
      leftOn &&= st.a!.onLine;
      rightOn &&= st.b!.onLine;
      if (st.a!.onLine) thick = Math.min(thick, st.a!.rail.thick);
      if (st.b!.onLine) thick = Math.min(thick, st.b!.rail.thick);
      // a side kept at the bank's width past a merge starts where the bank's own border left off
      // (that border ran on the ink, which is thicker where two lines meet) and eases onto its
      // set width over a couple of band widths: no step at the merge
      const run = Math.max(2 * st.width, 4 * this.tol);
      joinForward(left, st.mode === 'edge' && !st.a!.onLine ? continueFrom(left, g.left, run) : g.left);
      joinForward(right, st.mode === 'edge' && !st.b!.onLine ? continueFrom(right, g.right, run) : g.right);
    }
    flush();
    return out;
  }

  /** A free stretch's polygon: its path stroked with round joins so it can never spike. */
  #polygon(st: Stretch, g: Geometry, final: boolean): number[] {
    if (st.mode !== 'free') return joinSides(g.left, g.right);
    if (final && st.final && st.finalPoly) return st.finalPoly;
    const poly = strokePolygon(g.mid, st.width, Math.max(1, 0.1 * this.tol));
    if (final && st.final) st.finalPoly = poly;
    return poly;
  }

  /**
   * The stretches worth showing, in order. A short free or line stretch next to a
   * corridor is the finger arriving, overshooting or a misreading at a junction:
   * it is left out and the corridor's sides run straight on. The first stretch of
   * a stroke is not shown until it is long enough to mean something.
   */
  #assemble(final: boolean): { st: Stretch; g: Geometry }[] {
    const all = this.#closed.map((st) => ({ st, g: st.final! }));
    const open = this.#open;
    if (open) all.push({ st: open, g: open.mode === 'free' ? this.#freeGeometry(open, this.#raw.length, final) : this.#railGeometry(open, true) });
    const tol = this.tol;
    const len = (x: { g: Geometry }) => polylineLength(x.g.mid);
    const onRails = (x: { st: Stretch } | undefined) => !!x && x.st.mode !== 'free';
    const out: { st: Stretch; g: Geometry }[] = [];
    for (let i = 0; i < all.length; i++) {
      const cur = all[i]!;
      if (cur.st.mode !== 'mid') {
        const l = len(cur);
        const prev = out[out.length - 1];
        const next = all.slice(i + 1).find((x) => isCorridor(x.st.mode) || len(x) >= 5 * tol);
        // a short free or line stretch beside anything read from the plan is the finger arriving,
        // overshooting or a misread — never a mark of its own; between two corridors a misread
        // as long as a couple of band widths (a symbol on the edge, a leader's arrowhead) is bridged.
        // A bank kept at its own width past a merge is a mark of its own however short, unless a
        // corridor follows it (the finger went on out into the trench: the widening starts at the merge)
        const between = onRails(prev) && onRails(next) ? 2.5 * Math.max(prev!.st.width, next!.st.width) : 0;
        const tail = (cur.st.mode === 'edge' && !onRails(next)) || (cur.st.mode === 'snap' && prev?.st.mode === 'snap' && Math.abs(prev.st.a!.rail.thick - cur.st.a!.rail.thick) <= 0.3 * prev.st.a!.rail.thick && !next);
        if (!tail && (onRails(prev) || onRails(next)) && l < Math.max(5 * tol, between) && !(cur.st.mode === 'snap' && !onRails(prev) && !onRails(next))) continue;
        if (!prev && !next && l < 2 * tol) continue; // the stroke has barely begun
      }
      out.push(cur);
    }
    return out;
  }

  /**
   * Final clean band. Rail stretches keep their exact sides (only the raster
   * staircase is dropped); free stretches lose the finger jitter.
   */
  finish(): LiveBand {
    this.update();
    const tol = this.tol;
    const all = this.#assemble(true);
    const left: number[] = [],
      right: number[] = [],
      mid: number[] = [];
    const lens = new Map<LiveMode, number>();
    let midWidth = 0;
    const eps = 0.15; // the sides were read from the ink to a fraction of a pixel: keep that
    const luma = this.margin === 0 ? this.engine.luma : null;
    const cleaned: { st: Stretch; g: Geometry }[] = [];
    let anyFree = false;
    for (const { st, g } of all) {
      if (g.mid.length < 4) continue;
      lens.set(st.mode, (lens.get(st.mode) ?? 0) + polylineLength(g.mid));
      if (isCorridor(st.mode)) midWidth = Math.max(midWidth, st.width);
      const clean = st.mode === 'free' ? g : { left: simplify(g.left, eps), right: simplify(g.right, eps), mid: g.mid };
      // the join from the previous corridor stretch runs along the same lines (a bridged
      // misread, a rail fragment ending at a junction): it is read from the ink like the rest
      const prev = cleaned[cleaned.length - 1];
      if (luma && isCorridor(st.mode) && prev && isCorridor(prev.st.mode)) {
        if (st.a!.onLine && prev.st.a!.onLine) clean.left = bridgedSide(luma, prev.g.left, clean.left, 1, st.a!.rail.thick);
        if (st.b!.onLine && prev.st.b!.onLine) clean.right = bridgedSide(luma, prev.g.right, clean.right, -1, st.b!.rail.thick);
      }
      appendGeometry(clean, left, right, mid);
      if (st.mode === 'free') anyFree = true;
      cleaned.push({ st, g: clean });
    }
    const polys = this.#polys(cleaned, true);
    if (mid.length < 4 || left.length < 4) {
      const path = cleanPath(this.#raw, tol, 'free');
      return { pts: strokePolygon(path, this.width, Math.max(0.8, 0.05 * tol)), path, width: this.width, label: 'Free line' };
    }
    const total = polylineLength(mid) || 1;
    const path = simplify(mid, Math.max(1, tol * 0.08));
    // rail stretches alone join side to side exactly; with free parts the polygons are unioned
    // (the union's border snaps back onto the rails' sides, so they stay exact)
    const pts = anyFree ? dedupeClosed(unionPolygons(polys), Math.max(2, tol * 0.1)) : dedupeClosed(polys[0] ?? joinSides(left, right), Math.max(2, tol * 0.1));
    if ((lens.get('mid') ?? 0) + (lens.get('edge') ?? 0) >= 0.25 * total && midWidth) return { pts, path, width: midWidth, label: 'Between lines' };
    if ((lens.get('snap') ?? 0) >= 0.25 * total) return { pts, path, width: this.width, label: 'Along line' };
    return { pts, path, width: this.width, label: 'Free line' };
  }

  /* ------------------------------------------------------------ reading */

  /** Anchor hi-1 arrived: extend, cut back, close or replace the open stretch. */
  #advance(hi: number): void {
    let open = this.#open!;
    // the open stretch follows the finger: on along its rails, or back — out of the stretch
    // altogether and into the one before it, as far back as the finger goes
    for (let guard = 0; guard < 32; guard++) {
      const was = this.#open!;
      if (was.mode !== 'free') this.#validate(hi);
      else this.#scrubFree(hi);
      if (this.#open === was) break;
    }
    open = this.#open!;
    if (open.mode === 'snap' && open.waitFrom >= 0) {
      const cont = this.#lineGoingOn(open, hi);
      if (cont) {
        this.#closeRail(open, open.end, false);
        this.#openRail(cont, hi);
        return;
      }
    }
    // coming back inside a stretch already read further on, the finger is choosing where the band
    // ends, not what it follows: nothing is re-read until it is out at the frontier again
    if (open.mode !== 'free' && open.outSince < 0 && this.#behind(open) > this.tol) return;
    // Evidence is read over the window behind the finger even when it reaches back
    // into a closed stretch (a rail that ran alongside all along counts); the new
    // stretch itself can only begin where the open one does.
    // The stroke's first stretch, while still short, is read from its start, so a reading found
    // late (the app may read the tracer only some way into the stroke) still begins where the
    // finger began. A free stretch after a closed one is read over the full window at once: the
    // line that goes on past a merge has supported the finger all along, so the band follows the
    // finger through the merge without waiting for fresh evidence (the new stretch can still only
    // begin where the free one does).
    const lo = open.mode === 'free' && open.from === 0 && hi - open.from <= 3 * this.#window ? 0 : Math.max(0, hi - this.#window);
    if (hi - lo < this.#minEvidence) return;
    const found = this.#search(lo, hi, open.mode === 'mid' && open.outSince >= 0 ? open.end : open.from);
    if (!found) return;
    // a bank kept at its width learns of the trench around it as soon as one is read
    if (open.mode === 'edge' && found.mode === 'edge' && found.trench !== undefined && Number.isFinite(found.trench) && !Number.isFinite(open.trench)) open.trench = found.trench;
    if (open.mode === 'free') {
      this.#cutFree(open, found.j);
      this.#openRail(found, hi);
    } else if (open.mode === 'mid' && open.outSince >= 0 && found.mode === 'mid' && ![found.a.rail, found.b.rail].some((r) => sameLine(r, open.a!.rail) || sameLine(r, open.b!.rail))) {
      // the finger left the corridor and is inside a different bank (sharing none of its edges):
      // that was no slip (a short intrusion into a neighbour is bridged when the first corridor resumes)
      this.lastClose = `#${hi - 1} ${open.mode} left for another corridor (left at #${open.outSince})`;
      this.closes.push(this.lastClose);
      this.#closeRail(open, open.end, false);
      this.#openRail(found, hi);
    } else if (this.#supersedes(found, open, lo, hi)) {
      this.#closeRail(open, found.j, false);
      this.#openRail(found, hi);
    }
  }

  /**
   * Check the newest anchors against the open rail stretch. A rail ending or the
   * rails veering apart ends it at once. The finger slipping out sideways does
   * not: the band keeps pace along the rails while the finger is out, and only
   * when it stays out too far or too long does the stretch end — back at the
   * point where the finger left, so the excursion is read afresh.
   */
  #validate(hi: number): void {
    const open = this.#open!;
    const tol = this.tol;
    for (let i = open.checked + 1; i < hi; i++) {
      open.checked = i;
      // back past where this stretch began: the stretch is undone, the one before it goes on
      if (this.#closed.length && this.#behindStart(open, i)) {
        this.#pop(i);
        return;
      }
      const r = this.#railOk(open, i);
      if (!r.why && r.waiting) continue; // (a bold line waited for: the band stays where it was)
      if (!r.why) {
        open.end = i;
        open.misses = 0;
        open.outSince = -1;
        open.outTravel = 0;
        open.outMax = 0;
        if (r.diverging) {
          if (open.divergeSince < 0) open.divergeSince = i;
        } else open.divergeSince = -1;
        continue;
      }
      if (!r.lateral || r.endsSoon) {
        // a rail running out under a finger that is on its way back (its near end, where the
        // stretch came in) is the finger backing out of the stretch, not the line ending
        if (this.#closed.length && !r.lateral && this.#behind(open) > tol) {
          this.#pop(i);
          return;
        }
        if (++open.misses >= 2) {
          this.lastClose = `#${i} ${open.mode} ${r.why}`;
          this.closes.push(this.lastClose);
          // rails that parted: the stretch ends where they began to
          const endAt = r.veered && open.divergeSince >= 0 ? Math.max(open.from, open.divergeSince - 1) : open.end;
          if (open.mode === 'mid' && (r.why === 'left rail ended' || r.why === 'right rail ended' || r.veered || (r.endsSoon && r.why.startsWith('crossed')))) {
            // one edge of the bank ends or bends away (it merges into a wider trench): the other
            // goes on, and the band keeps its width along it while the finger keeps to it
            const leftGone = r.veered ? r.veered === 'A' : r.why === 'left rail ended' || r.why === 'crossed left rail';
            const survivor = leftGone ? open.b! : open.a!;
            this.#lastEnded = { rail: survivor.rail, bandSide: leftGone ? 'L' : 'R', width: open.width, thick: survivor.rail.thick, anchor: endAt, by: open.id };
          }
          this.#closeRail(open, endAt, true);
          return;
        }
        continue;
      }
      // A slip goes out and comes back; only travel spent not coming back counts against it.
      if (open.outSince < 0) {
        open.outSince = i;
        open.outTravel = 0;
        open.outMax = r.out;
      } else {
        // only travel spent moving farther out counts; running alongside or coming back does not
        if (r.out > open.outMax + 1) open.outTravel += Math.hypot(this.#ax[i]! - this.#ax[i - 1]!, this.#ay[i]! - this.#ay[i - 1]!);
        open.outMax = Math.max(open.outMax, r.out);
      }
      const across = open.mode === 'mid' ? Math.max(4 * tol, 1.5 * (open.width - open.a!.off - open.b!.off)) : Math.max(2.5 * tol, 1.5 * open.slack);
      const budget = Math.max(10 * tol, 2.5 * across);
      if (r.out > across || open.outTravel > budget) {
        this.lastClose = `#${i} ${open.mode} ${r.why} (left at #${open.outSince}, out ${r.out.toFixed(0)}, travelled ${open.outTravel.toFixed(0)})`;
        this.closes.push(this.lastClose);
        this.#closeRail(open, open.end, true);
        return;
      }
    }
  }

  #openRail(found: Found, hi: number): void {
    this.#coalesce(found);
    // continuing on a line the previous stretch was already on: never start behind where it
    // ended (a fold in the border), start where it left off
    const prev = this.#closed[this.#closed.length - 1];
    if (prev && prev.mode !== 'free') {
      for (const side of found.mode === 'snap' ? [found.a] : [found.a, found.b]) {
        for (const ps of prev.mode === 'snap' ? [prev.a!] : [prev.a!, prev.b!]) {
          if (!sameLine(side.rail, ps.rail)) continue;
          const pr = projectOnRail(side.rail, railPointAt(ps.rail, ps.sEnd), 6);
          if (!pr) continue;
          if (side.fwd ? side.s0 < pr.s : side.s0 > pr.s) side.s0 = pr.s;
        }
      }
    }
    // A side beginning on another rail fragment of the line the previous rail stretch's side
    // ended on (the line runs on past a junction the rail growth stopped at) begins abreast of
    // that end: the rail carries the border across the seam, never a chord cut across the curve.
    // (A short bank kept past its merge is dropped from the band once a corridor follows — the
    // widening starts at the merge — so it is skipped here too.)
    let back = this.#closed.length - 1,
      skipped = 0;
    const stub = (st: Stretch) => st.mode === 'edge' && (st.end - st.from) * this.#step <= 2.5 * Math.max(st.width, found.width);
    while (back >= 0 && (this.#closed[back]!.mode === 'free' || stub(this.#closed[back]!))) skipped += polylineLength(this.#closed[back--]!.final!.mid);
    const prevRail = back >= 0 && skipped <= Math.max(5 * this.tol, 2.5 * found.width) ? this.#closed[back]! : null;
    if (prevRail) {
      const widening = found.mode === 'mid' && found.width > prevRail.width + this.#resumedTol(prevRail.width);
      for (const side of found.mode === 'snap' ? [found.a] : [found.a, found.b]) {
        for (const ps of prevRail.mode === 'snap' ? [prevRail.a!] : [prevRail.a!, prevRail.b!]) {
          if (sameLine(side.rail, ps.rail)) continue;
          const endPt = railPointAt(ps.rail, ps.sEnd);
          let pr = projectOnRail(side.rail, endPt, 2.5); // the new rail runs right through the old end: one line
          let cap = 160;
          if (!pr && widening) {
            // a wider corridor taking over where a bank's edge ended (the merge): its far side
            // begins abreast of that end, so the widening is a square step there, not a slant
            pr = projectOnRail(side.rail, endPt, found.width + 4);
            cap = 3 * found.width;
          }
          if (!pr) continue;
          const gap = side.fwd ? side.s0 - pr.s : pr.s - side.s0;
          if (gap > 2 && gap <= cap) side.s0 = pr.s;
        }
      }
    }
    for (const side of found.mode === 'snap' ? [found.a] : [found.a, found.b]) side.sEnd = side.far = side.s0;
    this.#open = { id: this.#nextId++, mode: found.mode, from: found.j, width: found.width, a: found.a, b: found.b, end: found.j, checked: found.j, misses: 0, outSince: -1, outTravel: 0, outMax: 0, divergeSince: -1, trench: found.trench ?? Infinity, slack: found.slack ?? this.tol, waitFrom: -1, trenchVotes: 0, head: [], lockRaw: 0, final: null };
    this.closes.push(`#${hi - 1} open ${found.mode}${Math.round(found.width)} from #${found.j} (${this.#open.id}) on #${found.a.rail.id}${found.mode === 'snap' ? '' : `/#${found.b.rail.id}`} of ${JSON.stringify((this.lastDebug as { rails?: unknown } | null)?.rails ?? null)}`);
    this.#validate(hi);
  }

  /* ------------------------------------------------------- scrubbing back */

  /** How far the finger has come back from the furthest point the open stretch reached (0: at the frontier). */
  #behind(st: Stretch): number {
    const a = st.a!;
    return a.fwd ? a.far - a.sEnd : a.sEnd - a.far;
  }

  /** The finger at anchor i is back past where the stretch began on its rails (with a little margin, so a finger resting at a seam never flickers). */
  #behindStart(st: Stretch, i: number): boolean {
    const p = { x: this.#ax[i]!, y: this.#ay[i]! };
    const margin = Math.max(2, 0.5 * this.tol);
    const behind = (side: Side): boolean => {
      const pr = projectOnRail(side.rail, p, 3 * this.#widthCap);
      if (!pr) return false;
      // off the rail's near end, the finger is that much further behind
      const s = pr.s <= 1e-6 ? -pr.over : pr.s >= side.rail.len - 1e-6 ? side.rail.len + pr.over : pr.s;
      return side.fwd ? s < side.s0 - margin : s > side.s0 + margin;
    };
    // a corridor: either line reading the finger behind the start line will do (at a merge the
    // other line may curve away, and its arc length is no measure there)
    return st.mode === 'mid' ? behind(st.a!) || behind(st.b!) : behind(st.a!);
  }

  /** The finger at p is inside a closed rail stretch's band, short of its end by more than the seam margin. */
  #insideBand(st: Stretch, p: Vec): boolean {
    const margin = Math.max(2, 0.5 * this.tol);
    const A = st.a!,
      B = st.b!;
    const within = (side: Side, s: number) => (side.fwd ? s >= side.s0 - margin && s <= side.sEnd - margin : s <= side.s0 + margin && s >= side.sEnd + margin);
    const pa = projectOnRail(A.rail, p, 3 * this.#widthCap);
    if (!pa || !within(A, pa.s)) return false;
    if (st.mode === 'snap') return pa.d <= st.slack;
    if (st.mode === 'edge') {
      const inward = A.onLine ? -sideOffset(A, pa, p) : sideOffset(A, pa, p);
      const slack = Math.max(2, 0.3 * this.tol);
      return inward >= -slack && inward <= st.width + slack;
    }
    const pb = projectOnRail(B.rail, p, 3 * this.#widthCap);
    if (!pb || !within(B, pb.s)) return false;
    const slack = Math.max(3, 0.5 * this.tol);
    return sideOffset(A, pa, p) <= slack && sideOffset(B, pb, p) >= -slack;
  }

  /**
   * The finger has come back out of the open stretch: that stretch is undone and the one
   * before it goes on from the finger — a rail stretch with its reach at the finger, a free
   * stretch with its route cut back to the finger.
   */
  #pop(i: number): void {
    const gone = this.#open!;
    const prev = this.#closed.pop()!;
    this.lastClose = `#${i} ${gone.mode} (${gone.id}) undone: the finger came back past its start; ${prev.mode} (${prev.id}) goes on`;
    this.closes.push(this.lastClose);
    if (this.#lastEnded?.by === prev.id) this.#lastEnded = null;
    if (this.#lastMid?.by === prev.id) this.#lastMid = null;
    if (this.#lastLine?.by === prev.id) this.#lastLine = null;
    prev.end = i;
    prev.checked = i - 1; // the same anchor is read against the re-opened stretch too: a jump back may undo several
    prev.misses = 0;
    prev.outSince = -1;
    prev.outTravel = 0;
    prev.outMax = 0;
    prev.divergeSince = -1;
    prev.trenchVotes = 0;
    const p = { x: this.#ax[i]!, y: this.#ay[i]! };
    if (prev.mode === 'free') {
      prev.head = prev.final!.mid.slice();
      prev.lockRaw = this.#araw[i]!;
      prev.final = null;
      delete prev.finalPoly;
      this.#open = prev;
      this.#trimFree(prev, i);
      return;
    }
    prev.final = null;
    for (const side of prev.mode === 'snap' ? [prev.a!] : [prev.a!, prev.b!]) {
      const pr = projectOnRail(side.rail, p, Infinity);
      if (pr) side.sEnd = side.fwd ? Math.max(side.s0, pr.s) : Math.min(side.s0, pr.s);
    }
    this.#open = prev;
  }

  /** An open free stretch at anchor hi-1: back inside the rail stretch before it, that stretch goes on; back along its own route, the route is cut there. */
  #scrubFree(hi: number): void {
    const open = this.#open!;
    const i = hi - 1;
    if (i <= open.checked) return;
    open.checked = i;
    const prev = this.#closed[this.#closed.length - 1];
    if (prev && prev.mode !== 'free' && this.#insideBand(prev, { x: this.#ax[i]!, y: this.#ay[i]! })) {
      this.#pop(i);
      return;
    }
    this.#trimFree(open, i);
  }

  /**
   * A free stretch's route is cut back to the finger when the finger comes back along it: the
   * route up to the previous anchor is the reference, and an anchor that lands on it (within the
   * band's own half width) short of its end is the finger back on its own route — the band's end
   * is the finger. A route cut back to nothing undoes the stretch. (Anchors are a step apart, so
   * a finger resting at the route's end, jittering, never cuts anything.)
   */
  #trimFree(st: Stretch, i: number): void {
    const tol = this.tol;
    const rawEnd = Math.min(this.#raw.length, Math.max(st.lockRaw, i > 0 ? this.#araw[i - 1]! + 2 : st.lockRaw));
    const head = st.head;
    const src = head.length ? [head[head.length - 2]!, head[head.length - 1]!, ...this.#raw.slice(st.lockRaw, rawEnd)] : this.#raw.slice(st.lockRaw, rawEnd);
    const live = freeTail(src, tol);
    const pts = head.length > 2 ? [...head.slice(0, -2), ...live] : live;
    const total = polylineLength(pts);
    if (total < 1) return;
    const p = { x: this.#ax[i]!, y: this.#ay[i]! };
    const best = nearestOnPath(pts, p);
    // on the route, and short of its end (at the end the finger is simply going on)
    if (best.d > this.#freeReach(st) || best.s > total - Math.max(1, 0.25 * tol)) return;
    const cut = [...pts.slice(0, best.k + 2), best.x, best.y];
    if (polylineLength(cut) < 1 && this.#closed.length) {
      this.#pop(i);
      return;
    }
    st.head = cut;
    st.lockRaw = this.#araw[i]!;
  }

  /**
   * A new reading on the same lines as a recent stretch, with only a short
   * misreading between them (a symbol, a crossing line), continues that stretch
   * instead: the stretches between are dropped and the sides run straight through.
   */
  #coalesce(found: Found): void {
    const closed = this.#closed;
    let k = closed.length - 1,
      between = 0;
    while (k >= 0) {
      const st = closed[k]!;
      if (st.mode === found.mode && sameLine(st.a!.rail, found.a.rail) && sameLine(st.b!.rail, found.b.rail)) break;
      between += polylineLength(st.final!.mid);
      if (between > 5 * this.tol) return;
      k--;
    }
    if (k < 0) return;
    const base = closed[k]!;
    // the lines must coincide over the earlier stretch, not merely share a distant tail
    const starts: number[] = [];
    for (const [side, from] of [[found.a, base.a!], [found.b, base.b!]] as const) {
      for (const s of [from.s0, from.sEnd]) {
        const pr = projectOnRail(side.rail, railPointAt(from.rail, s), 6);
        if (!pr || pr.d > 3) return;
        if (s === from.s0) starts.push(pr.s);
      }
    }
    found.a.s0 = starts[0]!;
    found.b.s0 = starts[1]!;
    found.j = base.from;
    found.width = base.width; // the same lines: the band as first read, not as re-read at a junction
    closed.length = k;
  }

  /**
   * How the finger stands to the stretch's rails at anchor i: `why` is null while it
   * is between (or on) them; `lateral` says the finger merely slipped out sideways
   * (by `out` px) rather than a rail ending or the rails veering apart. Advances
   * the rails' reach either way, so the band keeps pace with the finger.
   */
  /** Anchors in ~3·tol of travel (anchors are #step apart): the finger's recent average, not a wobble. */
  get #recent(): number {
    return Math.max(3, Math.ceil((3 * this.tol) / this.#step));
  }

  /** The finger's distance from a kept-width stretch's line, into the band, averaged over its
   *  last ~3·tol of travel: a wobble is not a move. */
  #meanInward(A: Side, i: number, from: number): number {
    let sum = 0,
      n = 0;
    for (let k = i; k > i - this.#recent && k >= from && k >= 0; k--) {
      const p = { x: this.#ax[k]!, y: this.#ay[k]! };
      const pr = projectOnRail(A.rail, p, Infinity);
      if (!pr) continue;
      sum += A.onLine ? -sideOffset(A, pr, p) : sideOffset(A, pr, p);
      n++;
    }
    return n ? sum / n : 0;
  }

  /** Bank or trench, read over the finger's last ~3·tol of travel: `past` is how far the finger's
   *  distance from the kept line (into the band) runs past halfway between the bank's centre and
   *  the trench's, `half` how far the trench's own centre lies past that halfway, `W` the trench's
   *  width — all read at each anchor between the pair's rails, as the trench narrows or widens. */
  #pastMidway(A: Side, pair: [Rail, Rail], width: number, i: number, from: number): { past: number; half: number; W: number } | null {
    let past = 0,
      half = 0,
      W = 0,
      n = 0;
    for (let k = i; k > i - this.#recent && k >= from && k >= 0; k--) {
      const p = { x: this.#ax[k]!, y: this.#ay[k]! };
      const pr = projectOnRail(A.rail, p, Infinity),
        pa = projectOnRail(pair[0], p, Infinity),
        pb = projectOnRail(pair[1], p, Infinity);
      if (!pr || !pa || !pb) continue;
      const inward = A.onLine ? -sideOffset(A, pr, p) : sideOffset(A, pr, p);
      const w = pa.d + pb.d;
      past += inward - (width / 2 + w / 2) / 2;
      half += (w / 2 - width / 2) / 2;
      W += w;
      n++;
    }
    return n ? { past: past / n, half: half / n, W: W / n } : null;
  }

  /** A pair this close to a kept bank's width is that bank's own far edge back again (a gap, a symbol on it). */
  #resumedTol(width: number): number {
    return Math.max(2.5, 0.08 * width);
  }

  /** In doubt the bank keeps its width: the finger's average must run this far past the mark to mean the trench. */
  get #trenchBias(): number {
    return Math.max(1.5, 0.1 * this.tol);
  }

  /** A bank kept this far past its merge is what the finger meant: from here only a move past the
   *  trench's own centre widens it (a slow drift never does). */
  #settled(st: Stretch): boolean {
    return Math.abs(st.a!.sEnd - st.a!.s0) > Math.max(10 * this.tol, 3 * st.width);
  }

  #railOk(st: Stretch, i: number): { why: string | null; lateral: boolean; out: number; endsSoon?: boolean; veered?: 'A' | 'B'; diverging?: boolean; waiting?: boolean } {
    const p = { x: this.#ax[i]!, y: this.#ay[i]! };
    const tol = this.tol;
    const A = st.a!,
      B = st.b!;
    const ok = { why: null, lateral: false, out: 0 };
    // the projection onto a rail that runs alongside moves about as far as the finger did
    const maxAdvance = 4 * (i > 0 ? Math.hypot(p.x - this.#ax[i - 1]!, p.y - this.#ay[i - 1]!) : this.#step) + 6;
    if (st.mode === 'snap') {
      const pr = projectOnRail(A.rail, p, 3 * st.slack);
      if (!pr) return { why: 'off the line', lateral: true, out: 3 * st.slack };
      const past = pr.over > 0.5 * tol;
      // the line turning off from the finger's way while the finger goes on (it has run into a line
      // crossing it at a shallow angle, merged with it there): it has ended for this band — the line
      // going straight on is read next (a real bend is the same line again, and joins up)
      const veer = !past && pr.d > Math.max(2, 0.35 * tol) && this.#veered(st, i, pr);
      if (past || veer) {
        // A bold line's rail running out (or veering off) under a finger going straight on is a dash
        // lost in line work crossing it: the band waits where the line was last followed, for the
        // line going on to be read (#lineGoingOn), while the finger keeps within reach of its way
        const aside = past ? Math.sqrt(Math.max(0, pr.d * pr.d - pr.over * pr.over)) : pr.d;
        if (st.slack > tol && aside <= st.slack && this.#waited(st, i) <= BOLD_GAP * tol) {
          if (st.waitFrom < 0) st.waitFrom = i;
          return { ...ok, waiting: true };
        }
        return { why: past ? 'line ended' : 'line veered off', lateral: false, out: 0 };
      }
      st.waitFrom = -1;
      reach(A, pr.s, maxAdvance);
      if (pr.d > st.slack) return { why: 'off the line', lateral: true, out: pr.d };
      return ok;
    }
    if (st.mode === 'edge') {
      const pr = projectOnRail(A.rail, p, 3 * this.#widthCap);
      if (!pr) return { why: 'line lost', lateral: false, out: 0 };
      if (pr.over > 0.5 * tol) return { why: 'line ended', lateral: false, out: 0 };
      reach(A, pr.s, maxAdvance);
      reach(B, pr.s, maxAdvance);
      // how far into the band the finger is, from the line: crossing the line is a slip; going
      // out past the band's far side is the finger asking for the wider trench
      const inward = A.onLine ? -sideOffset(A, pr, p) : sideOffset(A, pr, p);
      const slack = Math.max(2, 0.3 * tol);
      if (inward < -slack) return { why: 'crossed the line', lateral: true, out: -inward };
      // past halfway between the bank's centre and the trench's, the finger means the trench: read
      // from the finger's recent average, so a wobble never widens a bank
      const mid = (st.width / 2 + st.trench / 2) / 2;
      const limit = !Number.isFinite(st.trench) ? st.width + slack : (this.#settled(st) ? st.trench / 2 : mid) + this.#trenchBias;
      const mean = this.#meanInward(A, i, st.from);
      if (mean > limit) return { why: 'left the bank for the trench', lateral: false, out: mean - limit };
      return ok;
    }
    const pa = projectOnRail(A.rail, p, 3 * this.#widthCap),
      pb = projectOnRail(B.rail, p, 3 * this.#widthCap);
    if (!pa || !pb) return { why: 'rails lost', lateral: false, out: 0 };
    if (pa.over > 0.5 * tol) return { why: 'left rail ended', lateral: false, out: 0 };
    if (pb.over > 0.5 * tol) return { why: 'right rail ended', lateral: false, out: 0 };
    reach(A, pa.s, maxAdvance);
    reach(B, pb.s, maxAdvance);
    const slack = Math.max(3, 0.5 * tol);
    // the two rails running into one line: the corridor is gone (a rail that wandered onto the other's line)
    const between = Math.hypot(pa.x - pb.x, pa.y - pb.y);
    if (between < 0.5 * (st.width - A.off - B.off)) return { why: `rails converge (${between.toFixed(0)} at ${p.x.toFixed(0)},${p.y.toFixed(0)})`, lateral: false, out: 0 };
    const offA = sideOffset(A, pa, p),
      offB = sideOffset(B, pb, p);
    // crossing a rail right where it ends (a bank merging into another) is the rail ending
    const remaining = (side: Side, pr: Projection) => (side.fwd ? side.rail.len - pr.s : pr.s);
    if (offA > slack) return { why: 'crossed left rail', lateral: true, out: offA, endsSoon: remaining(A, pa) < 3 * tol };
    if (offB < -slack) return { why: 'crossed right rail', lateral: true, out: -offB, endsSoon: remaining(B, pb) < 3 * tol };
    // the rails must still bracket the finger at about the corridor's width (a rail
    // veering away — a leader, a line that only ran alongside for a while, an edge bending off
    // into a wider trench — ends the stretch, back where the rails began to part)
    const apart = st.width - A.off - B.off;
    if (pa.d + pb.d > 1.5 * apart + 2 * slack) return { why: `rails diverge (${pa.d.toFixed(0)}+${pb.d.toFixed(0)} vs ${st.width.toFixed(0)} at ${p.x.toFixed(0)},${p.y.toFixed(0)})`, lateral: false, out: 0, veered: pa.d > pb.d ? 'A' : 'B' };
    if (pa.d + pb.d > apart + 2 * slack) return { ...ok, diverging: true };
    return ok;
  }

  /**
   * The followed line has turned off from the way it ran a few finger-widths back while the finger
   * kept to that way (a wobble turns neither; a real bend turns both).
   */
  #veered(st: Stretch, i: number, pr: Projection): boolean {
    const k = Math.max(st.from, i - Math.ceil((6 * this.tol) / this.#step));
    if (i - k < 3) return false;
    const back = projectOnRail(st.a!.rail, { x: this.#ax[k]!, y: this.#ay[k]! }, 3 * this.tol);
    if (!back) return false;
    if (Math.abs(back.tx * pr.tx + back.ty * pr.ty) >= VEER_COS) return false;
    const fx = this.#ax[i]! - this.#ax[k]!,
      fy = this.#ay[i]! - this.#ay[k]!;
    const fl = Math.hypot(fx, fy);
    return fl > 0 && Math.abs(fx * back.tx + fy * back.ty) / fl >= KEPT_COS;
  }

  /**
   * A rail is the followed line going on: where the finger is, it lies on the line's extension from
   * where the stretch last followed it (within the line's own weight), runs the same way, and goes
   * on ahead of the finger.
   */
  #goesOn(st: Stretch, side: Side, g: Projection): boolean {
    const A = st.a!;
    const e = railPointAt(A.rail, A.sEnd);
    const back = railPointAt(A.rail, A.fwd ? Math.max(0, A.sEnd - 2 * this.tol) : Math.min(A.rail.len, A.sEnd + 2 * this.tol));
    const tx = e.x - back.x,
      ty = e.y - back.y;
    const tl = Math.hypot(tx, ty);
    if (tl < 1e-6) return false;
    const ux = tx / tl,
      uy = ty / tl;
    // on the line's extension: at the finger, and all the way a few finger-widths on (a rail that
    // lies on the line only where it crosses it veers off soon after)
    const onExtension = (q: Vec): boolean => {
      const ahead = (q.x - e.x) * ux + (q.y - e.y) * uy;
      const off = Math.abs((q.x - e.x) * uy - (q.y - e.y) * ux);
      return off <= Math.max(0.6 * A.rail.thick, 0.25 * this.tol) + 0.05 * Math.max(0, ahead);
    };
    if (!onExtension(g)) return false;
    if (Math.abs(g.tx * ux + g.ty * uy) < Math.cos((12 * Math.PI) / 180)) return false;
    // (running on ahead of the finger along travel)
    const onward = side.fwd === (g.tx * ux + g.ty * uy > 0) ? (side.fwd ? side.rail.len - g.s : g.s) : 0;
    if (onward < 2 * this.tol) return false;
    for (let d = this.tol; d <= Math.min(onward, 4 * this.tol); d += this.tol) if (!onExtension(railPointAt(side.rail, side.fwd ? g.s + d : g.s - d))) return false;
    return true;
  }

  /** How far the finger has gone while a bold line's band has waited for it (0: not waiting). */
  #waited(st: Stretch, i: number): number {
    if (st.waitFrom < 0) return 0;
    let d = 0;
    for (let k = st.waitFrom + 1; k <= i; k++) d += Math.hypot(this.#ax[k]! - this.#ax[k - 1]!, this.#ay[k]! - this.#ay[k - 1]!);
    return d;
  }

  /**
   * A bold line waited for: the rail that carries it on — looked for along the line's own way from
   * where it was last followed (not among whatever the finger happens to be over: in a tangle of
   * line work crossing it the rails grown there wander off), of its weight, running its way, and
   * reaching back to the finger.
   */
  #lineGoingOn(st: Stretch, hi: number): Found | null {
    const A = st.a!;
    const tol = this.tol;
    const e = railPointAt(A.rail, A.sEnd);
    const back = railPointAt(A.rail, A.fwd ? Math.max(0, A.sEnd - 2 * tol) : Math.min(A.rail.len, A.sEnd + 2 * tol));
    const tl = Math.hypot(e.x - back.x, e.y - back.y);
    if (tl < 1e-6) return null;
    const ux = (e.x - back.x) / tl,
      uy = (e.y - back.y) / tl;
    const w = A.rail.thick;
    const finger = { x: this.#ax[hi - 1]!, y: this.#ay[hi - 1]! };
    const seen = new Set<number>([A.rail.id]);
    for (let k = 0; k <= (BOLD_GAP + 1) * tol; k += 0.5 * tol) {
      const p = { x: e.x + ux * k, y: e.y + uy * k };
      for (const id of this.engine.segsNear(p, 0.6 * w + 2)) {
        const r = this.engine.rails.railOf(id);
        if (seen.has(r.id)) continue;
        seen.add(r.id);
        if (r.loop || Math.abs(r.thick - w) > 0.3 * w) continue;
        const g = projectOnRail(r, finger, st.slack + 2);
        if (!g || g.over > 2) continue;
        const side: Side = { rail: r, fwd: g.tx * ux + g.ty * uy > 0, s0: g.s, sEnd: g.s, far: g.s, off: this.width / 2, onLine: false };
        if (!this.#goesOn(st, side, g)) continue;
        this.closes.push(`#${hi - 1} snap: #${A.rail.id} goes on along #${r.id}`);
        return { mode: 'snap', a: side, b: side, j: hi - 1, width: this.width, slack: st.slack };
      }
    }
    return null;
  }

  /** Close a rail stretch at anchor i (dropping it when it barely covered anything), then continue free from there. */
  #closeRail(st: Stretch, i: number, openFree: boolean): void {
    let tail: number[] = [];
    if (i - st.from < 3) i = st.from; // a reading that died at once was a misreading
    this.closes.push(`#${i} close ${st.mode} (${st.id}) from #${st.from}${i > st.from ? '' : ' (dropped)'}`);
    // … and is not read again from the same evidence: the finger must bring some more first
    if (i === st.from) this.#refused = { key: readingKey(st.mode, st.a!.rail, st.b!.rail), until: this.#ax.length + this.#minEvidence };
    if (i > st.from) {
      const p = { x: this.#ax[i]!, y: this.#ay[i]! };
      for (const side of st.mode === 'snap' ? [st.a!] : [st.a!, st.b!]) {
        const pr = projectOnRail(side.rail, p, Infinity);
        if (pr) side.sEnd = pr.s;
      }
      st.end = i;
      st.final = this.#railGeometry(st, false);
      if (st.final.mid.length >= 4) {
        this.#closed.push(st);
        tail = st.final.mid.slice(-2);
        if (st.mode === 'mid') this.#lastMid = { a: st.a!.rail, b: st.b!.rail, thick: Math.min(st.a!.rail.thick, st.b!.rail.thick), anchor: i, by: st.id };
        if (st.mode === 'snap') this.#lastLine = { rail: st.a!.rail, thick: st.a!.rail.thick, anchor: i, by: st.id };
      }
    }
    if (openFree) this.#open = this.#freeStretch(i, st.width, tail);
    else this.#open = null;
  }

  /** Close a free stretch at anchor j (the part before it stays; nothing stays when j is its start). */
  #cutFree(st: Stretch, j: number): void {
    this.#open = null;
    if (j <= st.from) return;
    st.end = j;
    st.final = this.#freeGeometry(st, this.#araw[j]! + 2, true);
    if (st.final.mid.length >= 4) this.#closed.push(st);
  }

  /** How close to its own route the finger must be to be back on it (a free stretch): inside the band's own half width (the route's corners are rounded a little, the finger's are not). */
  #freeReach(st: Stretch): number {
    return Math.max(2, 0.5 * st.width);
  }

  #freeStretch(from: number, width: number, head: number[]): Stretch {
    return { id: this.#nextId++, mode: 'free', from, width, a: null, b: null, end: from, checked: from, misses: 0, outSince: -1, outTravel: 0, outMax: 0, divergeSince: -1, trench: Infinity, slack: this.tol, waitFrom: -1, trenchVotes: 0, head, lockRaw: this.#araw[from] ?? 0, final: null };
  }

  /**
   * A better reading of the same ground: a corridor around a followed line, another line the
   * finger has plainly kept to instead of the one followed, or a narrower corridor inside the
   * current one.
   */
  #supersedes(found: Found, open: Stretch, lo: number, hi: number): boolean {
    if (open.mode === 'snap') {
      if (found.mode === 'mid' || found.mode === 'edge') return true; // a band read from the plan's lines beats a line under the finger
      // a bold line waited for past its rail's end: the line going on at its weight takes over there
      if (found.mode === 'snap' && open.slack > this.tol && !sameLine(found.a.rail, open.a!.rail) && Math.abs(found.a.rail.thick - open.a!.rail.thick) <= 0.3 * open.a!.rail.thick) {
        const at = { x: this.#ax[hi - 1]!, y: this.#ay[hi - 1]! };
        const f = projectOnRail(open.a!.rail, at, Infinity);
        const g = projectOnRail(found.a.rail, at, Infinity);
        if (f && g && (f.over > 0.25 * this.tol || f.d > g.d + Math.max(2, 0.3 * this.tol)) && this.#goesOn(open, found.a, g)) {
          this.closes.push(`#${hi - 1} snap goes on along #${found.a.rail.id} past the end of #${open.a!.rail.id}`);
          return true;
        }
      }
      return this.#keptToInstead(found, open, lo, hi);
    }
    if (found.mode !== 'mid') return false;
    if (open.mode === 'edge') {
      const r = this.#pastMidway(open.a!, [found.a.rail, found.b.rail], open.width, this.#ax.length - 1, open.from);
      if (!r) return false;
      if (r.W < 0.75 * open.width) return false; // line work inside the bank
      if (Math.abs(r.W - open.width) <= this.#resumedTol(open.width)) return true; // the bank's own pair resumed
      open.trench = r.W;
      // the finger nearer the trench's centre than the bank's, twice running, means the trench;
      // a bank kept well past the merge only widens once the finger runs past the trench's centre
      const over = this.#settled(open) ? r.half : 0;
      open.trenchVotes = r.past > over + this.#trenchBias ? open.trenchVotes + 1 : 0;
      return open.trenchVotes >= 2;
    }
    const same = sameLine(found.a.rail, open.a!.rail) && sameLine(found.b.rail, open.b!.rail);
    if (same || found.width >= 0.85 * open.width) return false;
    if (open.outSince >= 0) return false; // while the finger is out of it, a corridor is not re-read narrower
    // A corridor the finger has settled into is kept until a rail ends or the finger leaves:
    // a narrower pair may only replace a young reading (the stroke began just outside a bank).
    const covered = polylineLength(this.#railGeometry(open, true).mid);
    if (covered > 6 * this.tol) return false;
    // a narrower pair made of lines that cross the corridor's own edges is line work passing through it
    const region = this.#region;
    for (const r of [found.a.rail, found.b.rail]) for (const o of [open.a!.rail, open.b!.rail]) if (!sameLine(r, o) && railsCross(r, o, region)) return false;
    return true;
  }

  /**
   * A line only just taken, which the finger has since kept away from, keeping nearer another
   * (the line first taken crossed its route at a shallow angle — often right where the stroke
   * began — and runs away from it): that line is the one followed, from where the finger came
   * nearer to it; the stroke's first reading is replaced from where the finger began. A reading
   * the finger has followed a while is never given up this way (a wobble toward a line alongside
   * is not a change of mind): only the finger leaving it ends it.
   */
  #keptToInstead(found: Found, open: Stretch, lo: number, hi: number): boolean {
    const A = open.a!.rail,
      B = found.a.rail;
    if (sameLine(A, B)) return false;
    // a bold line is never given up for line work far lighter than it (the finger wobbling onto a
    // thin line alongside): only the finger leaving it ends it
    if (A.thick >= BOLD * B.thick) return false;
    const tol = this.tol;
    const margin = Math.max(1.5, 0.15 * tol);
    const dist = (r: Rail, i: number): number => {
      const pr = projectOnRail(r, { x: this.#ax[i]!, y: this.#ay[i]! }, this.#cap + 2);
      return pr && pr.over <= 2 ? pr.d : Infinity;
    };
    const lo2 = Math.max(lo, open.from);
    let k = hi;
    if ((found.slack ?? tol) > tol && B.thick >= BOLD * A.thick) {
      // a bold line within the finger's reach, the finger not keeping right on the thin line taken:
      // the bold line is the one meant, from where the finger has been within its reach
      let on = 0;
      for (let i = lo2; i < hi; i++) if (dist(A, i) <= 0.2 * tol) on++;
      if (on >= 0.6 * (hi - lo2)) return false;
      for (let i = hi - 1; i >= open.from; i--) {
        if (dist(B, i) <= found.slack!) k = i;
        else break;
      }
    } else {
      if (Math.abs(open.a!.sEnd - open.a!.s0) > 6 * tol) return false;
      // the finger must be off the line followed now, not merely nearer another alongside it
      if (dist(A, hi - 1) <= Math.max(2, 0.3 * tol)) return false;
      for (let i = hi - 1; i >= lo2; i--) {
        if (dist(B, i) + margin < dist(A, i)) k = i;
        else break;
      }
      // where the stroke began on both lines at once (the one first taken crossing the other there),
      // the misreading goes altogether when the new line was never the farther of the two
      if (hi - k >= this.#minEvidence) {
        let k2 = k;
        const first = !this.#closed.some((st) => st.mode !== 'free');
        while (k2 - 1 >= open.from && (dist(B, k2 - 1) <= dist(A, k2 - 1) + margin || (first && dist(B, k2 - 1) <= this.#lineReach))) k2--;
        if (k2 === open.from) k = k2;
      }
    }
    if (hi - k < this.#minEvidence) return false;
    const j = k === open.from ? open.from : Math.max(found.j, k);
    if (j !== found.j) {
      // the new line's side begins where the finger came to it
      let s0 = NaN;
      for (let i = j; i < hi; i++) {
        const pr = projectOnRail(B, { x: this.#ax[i]!, y: this.#ay[i]! }, this.#cap + 2);
        if (pr && (Number.isNaN(s0) || (found.a.fwd ? pr.s < s0 : pr.s > s0))) s0 = pr.s;
      }
      if (Number.isNaN(s0)) return false;
      found.a.s0 = found.a.sEnd = found.a.far = s0;
      found.j = j;
    }
    this.closes.push(`#${hi - 1} snap kept to #${B.id} instead of #${A.id} since #${k}`);
    return true;
  }

  /* ------------------------------------------------------------ search */

  /** Read the window [lo, hi) of anchors: which rails run alongside the finger, and what do they mean? A stretch found begins no earlier than anchor `from`. */
  #search(lo: number, hi: number, from: number): Found | null {
    const tol = this.tol;
    const rails = this.engine.rails;
    const cands = new Map<number, Rail>();
    for (let i = lo; i < hi; i += 2) {
      for (const id of this.engine.segsNear({ x: this.#ax[i]!, y: this.#ay[i]! }, this.#cap)) {
        const r = rails.railOf(id);
        cands.set(r.id, r);
      }
    }
    const evals: Eval[] = [];
    for (const r of cands.values()) {
      if (r.loop || r.len < 2 * tol) continue; // symbols and specks are not lines to follow
      const e = this.#evalRail(r, lo, hi);
      if (e) evals.push(e);
    }
    evals.sort((p, q) => p.d - q.d);
    // One line, one rail: rails grown from different pieces of the same line coincide. Where
    // they fork (a stray stub leaves an edge at a shallow angle) the one running along the
    // longer piece at the finger is the line; the stub loses.
    const finger = { x: this.#ax[hi - 1]!, y: this.#ay[hi - 1]! };
    const pieceAtFinger = (e: Eval): { len: number; thick: number } => {
      const pr = projectOnRail(e.rail, finger, this.#cap + 2);
      return pr ? railPieceAt(e.rail, pr.s, this.engine.plan) : { len: 0, thick: 0 };
    };
    // Rails that stayed on one line keep one weight; a rail that wandered onto another line
    // averages two. Among rails of the same line, prefer the longer piece under the finger,
    // then the weight closest to that piece's own, then length.
    const kept: Eval[] = [];
    for (const e of evals) {
      const dup = kept.find((k) => k.side === e.side && (Math.abs(k.d - e.d) <= 2.5 || sameLine(k.rail, e.rail)));
      if (!dup) kept.push(e);
      else {
        const pe = pieceAtFinger(e),
          pd = pieceAtFinger(dup);
        let better: boolean;
        if (pe.len > 1.5 * pd.len) better = true;
        else if (pd.len > 1.5 * pe.len) better = false;
        else {
          const de = Math.abs(e.rail.thick - pe.thick),
            dd = Math.abs(dup.rail.thick - pd.thick);
          better = Math.abs(de - dd) > 0.25 ? de < dd : e.rail.len > dup.rail.len;
        }
        if (better) kept[kept.indexOf(dup)] = e;
      }
    }
    // an edge of a corridor is a real line, not a short stray piece
    const edgeLen = 5 * tol;
    const L = kept.filter((e) => e.side === 'L' && !e.rail.dashed && e.rail.len >= edgeLen),
      R = kept.filter((e) => e.side === 'R' && !e.rail.dashed && e.rail.len >= edgeLen),
      O = kept.filter((e) => e.side === 'O');
    // a line under the finger lighter than the corridor just followed is incidental (never a line to follow)
    const lastThick = this.#lastMid && hi - this.#lastMid.anchor <= this.#window ? this.#lastMid.thick : 0;
    const onSolid = O.find((e) => !e.rail.dashed && e.rail.thick >= 0.7 * lastThick) ?? null;
    this.lastDebug = { lo, hi, rails: kept.map((e) => [e.side, +e.d.toFixed(1), e.rail.dashed ? 'd' : 's', +e.rail.thick.toFixed(1), `#${e.rail.id}:${Math.round(e.rail.pts[0]!)}→${Math.round(e.rail.pts[e.rail.pts.length - 2]!)}`, e.j - lo]) };
    const off = (e: Eval) => (e.side === 'O' ? 0 : e.d);
    // Line work crossing another line of the same weight nearby is not an edge of a corridor
    // (a utility line running through a duct bank at a shallow angle); corridor edges never cross.
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (let i = lo; i < hi; i++) {
      minX = Math.min(minX, this.#ax[i]!);
      maxX = Math.max(maxX, this.#ax[i]!);
      minY = Math.min(minY, this.#ay[i]!);
      maxY = Math.max(maxY, this.#ay[i]!);
    }
    const reachBox = 2 * this.#widthCap;
    const region: Box = { minX: minX - reachBox, minY: minY - reachBox, maxX: maxX + reachBox, maxY: maxY + reachBox };
    this.#region = region;
    const crossMemo = new Map<string, boolean>();
    const crosses = (p: Eval, q: Eval): boolean => {
      if (sameLine(p.rail, q.rail)) return false;
      const key = p.rail.id < q.rail.id ? `${p.rail.id}:${q.rail.id}` : `${q.rail.id}:${p.rail.id}`;
      let v = crossMemo.get(key);
      if (v === undefined) {
        v = railsCross(p.rail, q.rail, region);
        crossMemo.set(key, v);
      }
      return v;
    };
    const dashedBetween = (aD: number, bD: number) => kept.some((e) => e.rail.dashed && (e.side === 'O' || (e.side === 'L' && e.d < aD - 1) || (e.side === 'R' && e.d < bD - 1)));
    // A line under the finger (or between two edges) far bolder than the edges is what the finger
    // follows — a trench or a right of way drawn heavy, dashed or solid, with thin line work
    // running alongside it — never the centreline of a corridor (centrelines are the thin lines)
    const boldBetween = (a: Eval, b: Eval) => {
      const edge = Math.max(a.rail.thick, b.rail.thick);
      return kept.some((e) => e !== a && e !== b && e.rail.thick >= BOLD * edge && (e.side === 'O' || (e.side === a.side && e.d < a.d - 1) || (e.side === b.side && e.d < b.d - 1)));
    };
    const minW = Math.max(6, 0.5 * tol);
    // the weight of the line the finger is following, or followed a moment ago: across a symbol on
    // it or a gap in it, line work far lighter than it never makes a corridor
    const openLine = this.#open?.mode === 'snap' ? this.#open.a!.rail.thick : 0;
    const followedThick = Math.max(openLine, this.#lastLine && hi - this.#lastLine.anchor <= this.#window ? this.#lastLine.thick : 0);
    /**
     * Two solid lines are the edges of one corridor when they have the same weight,
     * keep their spacing, and no solid line of that weight runs between them (a
     * thinner line inside — a utility line, a leader — belongs to something else).
     */
    // The corridor just left (one of its edges ended, or a symbol pushed the finger out):
    // a pair that keeps one of its lines is the natural continuation and may change
    // width freely (a trench widening or narrowing right there).
    const last = this.#lastMid && hi - this.#lastMid.anchor <= this.#window ? this.#lastMid : null;
    const continues = (a: Eval, b: Eval) => !!last && (sameLine(a.rail, last.a) || sameLine(b.rail, last.b) || sameLine(a.rail, last.b) || sameLine(b.rail, last.a));
    const pairOk = (a: Eval, b: Eval): boolean => {
      const w = off(a) + off(b);
      if (w > this.#widthCap || w < minW) return false;
      const ta = a.rail.thick,
        tb = b.rail.thick;
      if (Math.min(ta, tb) < 0.55 * Math.max(ta, tb)) return false;
      if (crosses(a, b)) return false;
      if (boldBetween(a, b) || followedThick >= BOLD * Math.max(ta, tb)) return false;
      const wt = 0.7 * Math.min(ta, tb);
      // (a line between them that crosses both is line work passing through, not an edge)
      if (kept.some((e) => e !== a && e !== b && !e.rail.dashed && e.rail.thick >= wt && ((e.side === 'L' && e.d < off(a) - 1) || (e.side === 'R' && e.d < off(b) - 1)) && !(crosses(e, a) && crosses(e, b)))) return false;
      return parallelPair(a, b, this.#step, continues(a, b));
    };
    // line work crossing the corridor just followed (a utility band through a trench) is never its replacement
    const crossesLast = (e: Eval) => !!last && !sameLine(e.rail, last.a) && !sameLine(e.rail, last.b) && (railsCross(e.rail, last.a, region) || railsCross(e.rail, last.b, region));
    const pairs: [Eval, Eval][] = [];
    for (const a of L) for (const b of R) if (pairOk(a, b) && !crossesLast(a) && !crossesLast(b)) pairs.push([a, b]);
    // A corridor the finger runs just outside of (it slipped out before the reading settled):
    // two solid edges on the same side, a centreline between them, the nearer one close.
    const outsidePairs: [Eval, Eval][] = [];
    for (const side of [L, R]) {
      for (let i = 0; i < side.length; i++) {
        const near = side[i]!;
        if (near.d > 3 * tol) break;
        for (let k = i + 1; k < side.length; k++) {
          const far = side[k]!;
          const w = far.d - near.d;
          if (w < minW || w > this.#widthCap) continue;
          const ta = near.rail.thick,
            tb = far.rail.thick;
          if (Math.min(ta, tb) < 0.55 * Math.max(ta, tb)) continue;
          if (crosses(near, far) || crossesLast(near) || crossesLast(far) || boldBetween(near, far) || followedThick >= BOLD * Math.max(ta, tb)) continue;
          const wt = 0.7 * Math.min(ta, tb);
          if (side.some((e) => e !== near && e !== far && e.rail.thick >= wt && e.d > near.d + 1 && e.d < far.d - 1 && !(crosses(e, near) && crosses(e, far)))) continue;
          if (!kept.some((e) => e.rail.dashed && e.side === near.side && e.d > near.d + 1 && e.d < far.d - 1)) continue;
          if (!parallelPair(near, far, this.#step, continues(near, far))) continue;
          // the farther edge is the outer one: on the finger's left side it is the left rail
          outsidePairs.push(near.side === 'L' ? [far, near] : [near, far]);
        }
      }
    }
    const centred = ([a, b]: [Eval, Eval]) => dashedBetween(a.d, b.d) || Math.min(a.d, b.d) >= 0.3 * (a.d + b.d);
    const reads: [Eval, Eval][] = [];
    if (onSolid) {
      // A line under the finger that is lighter than the pair around it is incidental
      // (a utility line inside a duct bank): the pair is read as usual.
      for (const pr of pairs) if (onSolid.rail.thick < 0.7 * Math.min(pr[0].rail.thick, pr[1].rail.thick) && centred(pr)) reads.push(pr);
      // Otherwise the line is a centreline (a symmetric pair around it) …
      for (const pr of pairs) if (onSolid.rail.thick >= 0.7 * Math.min(pr[0].rail.thick, pr[1].rail.thick) && Math.abs(pr[0].d - pr[1].d) <= 0.25 * (pr[0].d + pr[1].d) && !dashedBetween(pr[0].d, pr[1].d)) reads.push(pr);
      // … or one edge of a corridor whose centreline runs beside the finger
      for (const b of R) if (pairOk(onSolid, b) && kept.some((e) => e.rail.dashed && e.side === 'R' && e.d < b.d - 1)) reads.push([onSolid, b]);
      for (const a of L) if (pairOk(a, onSolid) && kept.some((e) => e.rail.dashed && e.side === 'L' && e.d < a.d - 1)) reads.push([a, onSolid]);
    } else {
      for (const pr of pairs) if (centred(pr)) reads.push(pr);
    }
    const refused = this.#refused && hi < this.#refused.until ? this.#refused.key : null;
    const evidence = (a: Eval, b: Eval) => hi - Math.max(a.j, b.j) >= this.#minEvidence && readingKey('mid', a.rail, b.rail) !== refused;
    // continuity first, then the narrowest; a corridor the finger is inside of before one it is beside
    const span = ([a, b]: [Eval, Eval]) => (a.side === b.side ? 1000 + Math.abs(a.d - b.d) : off(a) + off(b));
    const forming = [...reads, ...outsidePairs];
    const ok = forming.filter(([a, b]) => evidence(a, b)).sort((p, q) => Number(continues(q[0], q[1])) - Number(continues(p[0], p[1])) || span(p) - span(q));
    // A bank that just lost one edge goes on at its own width along the other while the finger
    // keeps inside that width — the wider trench around it is what the finger gets by moving out
    // into it. A real pair of about the old width (the edge resumes) is that bank itself.
    const ended = this.#lastEnded && hi - this.#lastEnded.anchor <= 2 * this.#window ? this.#lastEnded : null;
    if (ended && readingKey('edge', ended.rail, ended.rail) !== refused) {
      const cont = this.#edgeContinuation(ended, lo, hi, from);
      if (cont) {
        // pairs narrower than the bank are line work inside it, never the bank or the trench
        const pair = ok.find(([a, b]) => off(a) + off(b) >= 0.75 * ended.width);
        if (!pair) return cont;
        const since = Math.max(from, ended.anchor);
        const r = this.#pastMidway(cont.a, [pair[0].rail, pair[1].rail], ended.width, hi - 1, since);
        const W = r?.W ?? off(pair[0]) + off(pair[1]);
        if (W < 0.75 * ended.width) return cont;
        // the bank's own pair resumed: that is the bank; otherwise the finger says which it means —
        // nearer the bank's centre than the trench's (or too soon to tell), it keeps the bank's
        // width; a corridor found later takes over once the finger has moved out (#supersedes)
        if (Math.abs(W - ended.width) > this.#resumedTol(ended.width)) {
          if (!r || hi - since < this.#recent || r.past <= this.#trenchBias) {
            cont.trench = W;
            return cont;
          }
          return this.#corridor(pair[0], pair[1], from, hi);
        }
      }
    }
    if (ok.length) return this.#corridor(ok[0]![0], ok[0]![1], from, hi);
    const lineOk = (e: Eval) => hi - e.j >= this.#minEvidence && readingKey('snap', e.rail, e.rail) !== refused;
    // The line the finger follows is the one it has kept nearest to over the window — a line
    // crossing its route at a shallow angle is near it only for a moment. Of lines about as near,
    // a finger means the boldest (a heavy trench line with thin line work along it); then a solid
    // one, then the longer.
    const prevLine = this.#open?.mode === 'snap' ? this.#open.a!.rail : this.#lastLine && hi - this.#lastLine.anchor <= this.#window ? this.#lastLine.rail : null;
    const followed = (cands: Eval[]): Eval | null => {
      const fit = cands.filter(lineOk);
      if (!fit.length) return null;
      // the line just followed, going on (a rail grown from another piece of it reaches further): that
      // line still, before any other near it
      // (and still that line where the finger is: a rail that shares the line behind but wandered off
      // it onto another is no continuation)
      const same = (e: Eval) => {
        const piece = pieceAtFinger(e);
        return piece.len > 0 && piece.thick >= 0.66 * prevLine!.thick && piece.thick <= prevLine!.thick / 0.66;
      };
      const cont = prevLine ? fit.find((e) => sameLine(e.rail, prevLine) && e.d <= this.#lineReach && same(e)) : undefined;
      if (cont) return cont;
      const best = Math.min(...fit.map((e) => e.d));
      const nearest = fit.find((e) => e.d === best)!;
      // (only a clearly bolder line outranks a nearer one — anywhere within the finger's reach; of
      // lines of a weight, the nearest)
      const close = fit.filter((e) => e.d <= best + Math.max(2, 0.3 * tol) || (e.rail.thick >= BOLD * nearest.rail.thick && e.d <= this.#lineReach));
      close.sort((p, q) => q.rail.thick - p.rail.thick || Number(p.rail.dashed) - Number(q.rail.dashed) || q.rail.len - p.rail.len);
      const top = close[0]!;
      return top.rail.thick >= BOLD * nearest.rail.thick ? top : nearest;
    };
    // A line drawn heavy (a trench, a right of way) stands out from the line work round it: a finger
    // means it from further off than a thin line — about two finger-widths — and even with thin lines
    // nearer the finger. It must be clearly bolder than the lines round it and than every line
    // nearer the finger, and the finger not right on one of those.
    const boldReach = 2 * tol;
    const bolds = kept.filter((e) => {
      if (e.d > boldReach || !lineOk(e)) return false;
      const others = kept.filter((o) => o !== e && !sameLine(o.rail, e.rail) && o.d <= this.#cap);
      const thicks = others.map((o) => o.rail.thick).sort((x, y) => x - y);
      const ref = thicks.length ? thicks[thicks.length >> 1]! : 0.8 * this.engine.rails.heavy;
      return e.rail.thick >= BOLD * ref && others.every((o) => o.d >= e.d || e.rail.thick >= BOLD * o.rail.thick);
    });
    const bold = bolds.sort((x, y) => x.d - y.d)[0];
    if (bold && !kept.some((o) => o !== bold && o.side === 'O' && o.d <= 0.2 * tol && !sameLine(o.rail, bold.rail))) return this.#line(bold, from, hi, Math.max(tol, boldReach));
    const under = followed(O.filter((e) => e.rail.dashed || e.rail.thick >= 0.7 * lastThick));
    // a far bolder line beside the finger, within its reach, is what it follows rather than line work
    // under it (the finger riding a little off a heavy trench line, on a thin line along it)
    const beside = under ? followed(kept.filter((e) => e.side !== 'O' && e.d <= this.#lineReach && e.rail.thick >= BOLD * under.rail.thick)) : null;
    if (beside) return this.#line(beside, from, hi);
    if (under) return this.#line(under, from, hi);
    // Nothing else explains the stroke: the nearest line running alongside within the finger's
    // reach is the line being followed, whichever side of it the finger happens to be on. (A
    // corridor taking shape around the finger is waited for rather than one of its edges taken.)
    if (forming.length) return null;
    const near = followed(kept.filter((e) => e.d <= this.#lineReach));
    if (near) return this.#line(near, from, hi);
    return null;
  }

  /** How one rail relates to the finger over the anchors [lo, hi). */
  #evalRail(rail: Rail, lo: number, hi: number): Eval | null {
    const n = hi - lo;
    const ok = new Uint8Array(n);
    const dist = new Float32Array(n);
    const tx = new Float32Array(n),
      ty = new Float32Array(n);
    let okCount = 0,
      dirSum = 0,
      pos = 0,
      neg = 0,
      ended = false;
    const ds: number[] = [];
    const px = new Float32Array(n),
      py = new Float32Array(n);
    for (let i = lo; i < hi; i++) {
      const p = { x: this.#ax[i]!, y: this.#ay[i]! };
      const pr = projectOnRail(rail, p, this.#cap + 2);
      if (pr && pr.over > 2 && i >= hi - 3) ended = true; // the finger has passed the rail's end
      if (!pr || pr.d > this.#cap || pr.over > 2) continue;
      const t = this.#tangent(i);
      const dot = t.x * pr.tx + t.y * pr.ty;
      if (Math.abs(dot) < PARALLEL_COS) continue;
      ok[i - lo] = 1;
      dist[i - lo] = pr.d;
      tx[i - lo] = pr.tx;
      ty[i - lo] = pr.ty;
      px[i - lo] = pr.x;
      py[i - lo] = pr.y;
      okCount++;
      dirSum += Math.sign(dot);
      ds.push(pr.d);
    }
    if (okCount < 3 || ended) return null;
    // which side of the finger the rail runs on, read against the rail's own direction turned the
    // way the finger travels on the whole: the finger running back over its route for a while
    // does not turn a line on its left into one on its right
    const fwdSign = dirSum >= 0 ? 1 : -1;
    for (let k = 0; k < n; k++) {
      if (!ok[k] || dist[k]! <= 1.5) continue;
      const rx = fwdSign * tx[k]!,
        ry = fwdSign * ty[k]!;
      const cross = rx * (py[k]! - this.#ay[lo + k]!) - ry * (px[k]! - this.#ax[lo + k]!);
      if (cross > 0) pos++;
      else neg++;
    }
    if (!ok[n - 1] && !ok[n - 2] && !ok[n - 3]) return null; // the rail must reach the finger
    let j = hi - 1,
      miss = 0;
    for (let i = hi - 1; i >= lo; i--) {
      if (ok[i - lo]) {
        j = i;
        miss = 0;
      } else if (++miss > 2) break;
    }
    ds.sort((a, b) => a - b);
    const d = ds[ds.length >> 1]!;
    const onR = Math.max(2, 0.35 * this.tol);
    let side: Eval['side'];
    if (d <= onR) side = 'O';
    else if (pos >= 0.85 * (pos + neg)) side = 'L';
    else if (neg >= 0.85 * (pos + neg)) side = 'R';
    else if (d <= this.#lineReach) side = 'O'; // the finger weaves across it: it is the line under the finger
    else return null; // the finger crosses it
    let first = -1,
      last = -1;
    for (let k = 0; k < n; k++) if (ok[k]) {
      if (first < 0) first = k;
      last = k;
    }
    return { rail, side, d, dFirst: dist[first]!, dLast: dist[last]!, j, fwd: dirSum >= 0, ok, tx, ty };
  }

  /** The bank going on at its old width along the line that survived, when the finger is still inside that width. */
  #edgeContinuation(ended: Ended, lo: number, hi: number, from: number): Found | null {
    const e = this.#evalRail(ended.rail, lo, hi);
    if (!e) return null;
    // the finger inside the band sees the line on the band's other side
    if (e.side !== 'O' && e.side !== (ended.bandSide === 'L' ? 'R' : 'L')) return null;
    if (e.d > ended.width + Math.max(3, 0.5 * this.tol)) return null;
    if (hi - e.j < this.#minEvidence) return null;
    const j = Math.max(e.j, from);
    const edge = edgeOffset(ended.thick);
    const far = Math.max(edge + 2, ended.width - edge);
    // a is the left side of travel, b the right: the line is one of them, the other runs at the old width
    const lineOnLeft = ended.bandSide === 'R';
    const a = this.#side(e, j, hi, lineOnLeft ? edge : far, lineOnLeft),
      b = this.#side(e, j, hi, lineOnLeft ? far : edge, !lineOnLeft);
    return { mode: 'edge', a, b, j, width: a.off + b.off, dist: e.dLast, trench: Infinity };
  }
  #corridor(a: Eval, b: Eval, from: number, hi: number): Found {
    const j = Math.max(a.j, b.j, from);
    const p = { x: this.#ax[j]!, y: this.#ay[j]! };
    // the border sits just inside the outer edge of each line (Ricky's rule), plus any margin
    const A = this.#side(a, j, hi, edgeOffset(a.rail.thick) + this.margin),
      B = this.#side(b, j, hi, edgeOffset(b.rail.thick) + this.margin);
    // rail to rail: the spacing read where the finger is inside both rails (a projection clamped
    // at a rail's end, at a merge, would read the diagonal), the median over the window
    const spans: number[] = [];
    for (let i = j; i < hi; i++) {
      const q = { x: this.#ax[i]!, y: this.#ay[i]! };
      const pa = projectOnRail(a.rail, q, this.#cap + 2),
        pb = projectOnRail(b.rail, q, this.#cap + 2);
      if (pa && pb && pa.over === 0 && pb.over === 0) spans.push(pa.d + pb.d);
    }
    spans.sort((x, y) => x - y);
    const qa = projectOnRail(a.rail, p, Infinity)!,
      qb = projectOnRail(b.rail, p, Infinity)!;
    const span = spans.length ? spans[spans.length >> 1]! : Math.hypot(qa.x - qb.x, qa.y - qb.y);
    const width = span + A.off + B.off;
    return { mode: 'mid', a: A, b: B, j, width };
  }
  #line(e: Eval, from: number, hi: number, slack = this.tol): Found {
    const j = Math.max(e.j, from);
    const S = this.#side(e, j, hi, this.width / 2, false);
    return { mode: 'snap', a: S, b: S, j, width: this.width, slack };
  }
  /** A side on a rail, beginning at the furthest back the finger supported it over anchors [j, hi) — the finger may have run back and forth there, and a stretch never begins ahead of where it is. */
  #side(e: Eval, j: number, hi: number, off: number, onLine = true): Side {
    let s0 = NaN;
    for (let i = j; i < hi; i++) {
      const pr = projectOnRail(e.rail, { x: this.#ax[i]!, y: this.#ay[i]! }, this.#cap + 2);
      if (!pr) continue;
      if (Number.isNaN(s0) || (e.fwd ? pr.s < s0 : pr.s > s0)) s0 = pr.s;
    }
    if (Number.isNaN(s0)) s0 = projectOnRail(e.rail, { x: this.#ax[j]!, y: this.#ay[j]! }, Infinity)!.s;
    return { rail: e.rail, fwd: e.fwd, s0, sEnd: s0, far: s0, off, onLine };
  }

  /** Direction of travel at anchor i, measured over about ±3·tol of route so finger jitter and jerks do not swing it. */
  #tangent(i: number): Vec {
    const n = this.#ax.length;
    const k = Math.max(2, Math.ceil((3 * this.tol) / this.#step));
    const a = Math.max(0, i - k),
      b = Math.min(n - 1, i + k);
    const dx = this.#ax[b]! - this.#ax[a]!,
      dy = this.#ay[b]! - this.#ay[a]!;
    const l = Math.hypot(dx, dy);
    if (l < 1e-6) return { x: 1, y: 0 };
    return { x: dx / l, y: dy / l };
  }

  /* ------------------------------------------------------------ geometry */

  /** Sides of a rail stretch: each rail between its start and its reach, offset to the border. */
  #railGeometry(st: Stretch, live: boolean): Geometry {
    const A = st.a!,
      B = st.b!;
    const finger = live ? { x: this.#raw[this.#raw.length - 2]!, y: this.#raw[this.#raw.length - 1]! } : null;
    const last = { x: this.#ax[st.end]!, y: this.#ay[st.end]! };
    // a slice never ends in a hook (the skeleton bending into a leader or a symbol right where the
    // stretch was cut): such an end is dropped, and the seam to the next stretch starts clean
    let pa = trimHooks(this.#sidePts(A, finger, last));
    let pb = st.mode === 'snap' || st.mode === 'edge' ? pa : trimHooks(this.#sidePts(B, finger, last));
    if (pa.length < 4 || pb.length < 4) return { left: [], right: [], mid: [] };
    // the band is cut square at both ends: where the two lines are not parallel (one bending
    // off into a merge, a trench tapering) the finger projects further along one of them, and
    // that side is cut abreast of the other
    if (st.mode === 'mid') [pa, pb] = squareEnds(pa, pb);
    // a side on a line is that line's outer edge, read from the ink itself where it is clean
    const luma = this.engine.luma && this.margin === 0 ? this.engine.luma : null;
    const left = luma && A.onLine ? refineSide(luma, pa, 1, A.rail.thick) : offsetSide(pa, A.off),
      right = luma && B.onLine ? refineSide(luma, pb, -1, B.rail.thick) : offsetSide(pb, -B.off);
    const mid = st.mode === 'snap' ? pa.slice() : st.mode === 'edge' ? averageSides(left, right, Math.max(4, this.tol / 2)) : averageSides(pa, pb, Math.max(4, this.tol / 2));
    return { left, right, mid };
  }

  /** The rail's polyline from the stretch start to its reach (extended to the finger while live), in travel order. */
  #sidePts(side: Side, finger: Vec | null, last: Vec): number[] {
    let s1 = side.sEnd;
    if (finger) {
      // the reach follows the finger itself between anchors, on or back, by at most what it moved
      const pr = projectOnRail(side.rail, finger, 2 * this.#widthCap);
      const room = 4 * Math.hypot(finger.x - last.x, finger.y - last.y) + 6;
      if (pr && pr.over <= 0.5 * this.tol) s1 = Math.max(s1 - room, Math.min(s1 + room, pr.s));
      s1 = side.fwd ? Math.max(s1, side.s0) : Math.min(s1, side.s0);
    }
    const pts = railSlice(side.rail, Math.min(side.s0, s1), Math.max(side.s0, s1));
    if (!side.fwd) reverseFlat(pts);
    return pts;
  }

  /** The finger's route for a free stretch, de-jittered; the part well behind the finger is locked. */
  #freeGeometry(st: Stretch, rawEnd: number, final: boolean): Geometry {
    const tol = this.tol;
    const head = st.head;
    const src = head.length ? [head[head.length - 2]!, head[head.length - 1]!, ...this.#raw.slice(st.lockRaw, rawEnd)] : this.#raw.slice(st.lockRaw, rawEnd);
    const live = freeTail(src, tol);
    let pts = head.length > 2 ? [...head.slice(0, -2), ...live] : live;
    if (!final && head.length >= 4) {
      // the finger back on the route behind it: the route is cut at the finger and the tail (the
      // finger's last steps back over the route) left out, so the band never doubles back on itself
      const q = nearestOnPath(head, { x: this.#raw[rawEnd - 2]!, y: this.#raw[rawEnd - 1]! });
      if (q.d <= this.#freeReach(st) && q.s < polylineLength(head) - Math.max(1, 0.25 * tol)) {
        const cut = [...head.slice(0, q.k + 2), q.x, q.y];
        return { left: offsetSide(cut, st.width / 2), right: offsetSide(cut, -st.width / 2), mid: cut };
      }
    }
    if (!final && polylineLength(live) > 6 * tol) {
      // lock everything but the last 3·tol so the path behind the finger stays put
      const cut = polylineLength(live) - 3 * tol;
      let acc = 0,
        k = 0;
      while (k + 3 < live.length) {
        const l = Math.hypot(live[k + 2]! - live[k]!, live[k + 3]! - live[k + 1]!);
        if (acc + l >= cut) break;
        acc += l;
        k += 2;
      }
      const l = Math.hypot(live[k + 2]! - live[k]!, live[k + 3]! - live[k + 1]!) || 1;
      const t = (cut - acc) / l;
      const cx = live[k]! + (live[k + 2]! - live[k]!) * t,
        cy = live[k + 1]! + (live[k + 3]! - live[k + 1]!) * t;
      st.head = [...(head.length > 2 ? head.slice(0, -2) : []), ...live.slice(0, k + 2), cx, cy];
      let best = st.lockRaw,
        bd = Infinity;
      for (let i = st.lockRaw; i < rawEnd; i += 2) {
        const d = Math.hypot(this.#raw[i]! - cx, this.#raw[i + 1]! - cy);
        if (d < bd) {
          bd = d;
          best = i;
        }
      }
      st.lockRaw = best;
    }
    if (final) pts = cleanPath(pts, tol, 'free');
    // a finger that has stalled leaves the path's last two points on top of each other, and a
    // zero-length last segment gives the stroker's flat end no direction: such a point goes
    while (pts.length >= 4 && Math.hypot(pts[pts.length - 2]! - pts[pts.length - 4]!, pts[pts.length - 1]! - pts[pts.length - 3]!) < 0.5) pts = pts.slice(0, -2);
    return { left: offsetSide(pts, st.width / 2), right: offsetSide(pts, -st.width / 2), mid: pts };
  }
}

/* ------------------------------------------------------------- helpers */

const HOOK_COS = Math.cos((25 * Math.PI) / 180);
const HOOK_LEN = 16; // image px: a turn this close to a slice's end is a hook, never the line bending

/**
 * Two sides of a band cut square at both ends: at each end the side that runs on past the
 * other (more than ~10° off square) is cut abreast of it — at the far end the one ahead, at
 * the start the one behind. Parallel lines are untouched.
 */
export function squareEnds(a: number[], b: number[]): [number[], number[]] {
  if (a.length < 4 || b.length < 4) return [a, b];
  const dirAt = (pts: number[], atEnd: boolean): Vec => {
    // the side's direction over its last (or first) few px
    const n = pts.length;
    let k = atEnd ? n - 4 : 2;
    const ex = atEnd ? pts[n - 2]! : pts[0]!,
      ey = atEnd ? pts[n - 1]! : pts[1]!;
    while (atEnd ? k > 0 : k < n - 2) {
      if (Math.hypot(pts[k]! - ex, pts[k + 1]! - ey) >= 3) break;
      k += atEnd ? -2 : 2;
    }
    const dx = atEnd ? ex - pts[k]! : pts[k]! - ex,
      dy = atEnd ? ey - pts[k + 1]! : pts[k + 1]! - ey;
    const l = Math.hypot(dx, dy) || 1;
    return { x: dx / l, y: dy / l };
  };
  const cutEnd = (pts: number[], to: Vec): number[] => {
    // the side cut where it comes abreast of `to` (its point nearest to it)
    let best = Infinity,
      bi = -1,
      bx = 0,
      by = 0;
    for (let i = 0; i + 3 < pts.length; i += 2) {
      const q = nearestOnSegment(to, { x: pts[i]!, y: pts[i + 1]! }, { x: pts[i + 2]!, y: pts[i + 3]! });
      if (q.d < best) {
        best = q.d;
        bi = i;
        bx = q.p.x;
        by = q.p.y;
      }
    }
    if (bi < 0) return pts;
    const out = pts.slice(0, bi + 2);
    if (Math.hypot(bx - out[out.length - 2]!, by - out[out.length - 1]!) > 0.3) out.push(bx, by);
    return out.length >= 4 ? out : pts;
  };
  const cutStart = (pts: number[], to: Vec): number[] => {
    const r = pts.slice();
    reverseFlat(r);
    const c = cutEnd(r, to);
    reverseFlat(c);
    return c;
  };
  const OFF = 0.17; // sin 10°
  for (const atEnd of [true, false]) {
    const ta = dirAt(a, atEnd),
      tb = dirAt(b, atEnd);
    const tx = ta.x + tb.x,
      ty = ta.y + tb.y;
    const tl = Math.hypot(tx, ty) || 1;
    const ax = atEnd ? a[a.length - 2]! : a[0]!,
      ay = atEnd ? a[a.length - 1]! : a[1]!,
      bx = atEnd ? b[b.length - 2]! : b[0]!,
      by = atEnd ? b[b.length - 1]! : b[1]!;
    const ex = bx - ax,
      ey = by - ay;
    const el = Math.hypot(ex, ey);
    if (el < 1e-6) continue;
    const off = (ex * tx + ey * ty) / (tl * el); // > 0: b's end is ahead of a's along travel
    if (Math.abs(off) <= OFF) continue;
    const bAhead = off > 0;
    if (atEnd) {
      if (bAhead) b = cutEnd(b, { x: ax, y: ay });
      else a = cutEnd(a, { x: bx, y: by });
    } else {
      // at the start the side that begins earlier (behind the other) is cut back to abreast
      if (bAhead) a = cutStart(a, { x: bx, y: by });
      else b = cutStart(b, { x: ax, y: ay });
    }
  }
  return [a, b];
}

/** A rail slice with any hook at either end removed (a sharp turn within the last few px of the slice). */
export function trimHooks(pts: number[]): number[] {
  let out = pts;
  for (let pass = 0; pass < 2; pass++) {
    out = trimEnd(out);
    reverseFlat(out);
  }
  return out;
}
function trimEnd(pts: number[]): number[] {
  let p = pts.slice();
  for (let guard = 0; guard < 4; guard++) {
    const n = p.length / 2;
    if (n < 3) return p;
    const ex = p[2 * n - 2]!,
      ey = p[2 * n - 1]!,
      qx = p[2 * n - 4]!,
      qy = p[2 * n - 3]!;
    const tail = Math.hypot(ex - qx, ey - qy);
    if (tail > HOOK_LEN) return p;
    // the direction over the 12 px before the last segment
    let acc = 0,
      k = n - 2;
    while (k > 0 && acc < 12) {
      acc += Math.hypot(p[2 * k]! - p[2 * k - 2]!, p[2 * k + 1]! - p[2 * k - 1]!);
      k--;
    }
    const bx = qx - p[2 * k]!,
      by = qy - p[2 * k + 1]!;
    const bl = Math.hypot(bx, by);
    if (bl < 4) return p;
    const c = ((ex - qx) * bx + (ey - qy) * by) / ((tail || 1) * bl);
    if (c >= HOOK_COS) return p;
    p = p.slice(0, 2 * n - 2);
  }
  return p;
}

/** A stretch read as a band between (or along) the plan's lines with a real width. */
function isCorridor(mode: LiveMode): boolean {
  return mode === 'mid' || mode === 'edge';
}

/** Move a side's reach to where the finger projects, on or back, by at most `maxAdvance` per anchor (a rail running away from the finger cannot drag the band with it). */
function reach(side: Side, s: number, maxAdvance: number): void {
  side.sEnd = Math.max(side.sEnd - maxAdvance, Math.min(side.sEnd + maxAdvance, s));
  // never behind the start: the start is where the stroke began, the finger sets only the end
  side.sEnd = side.fwd ? Math.max(side.sEnd, side.s0) : Math.min(side.sEnd, side.s0);
  side.far = side.fwd ? Math.max(side.far, side.sEnd) : Math.min(side.far, side.sEnd);
}

/** Signed offset of p from the rail at its projection, positive on the border's "left" side (offsetSide's positive side), with the rail oriented along travel. */
function sideOffset(side: Side, pr: Projection, p: Vec): number {
  const tx = side.fwd ? pr.tx : -pr.tx,
    ty = side.fwd ? pr.ty : -pr.ty;
  return tx * (p.y - pr.y) - ty * (p.x - pr.x);
}

/** What a reading is made of: its mode and lines (a refused reading is recognised by this). */
function readingKey(mode: LiveMode, a: Rail, b: Rail): string {
  return `${mode}:${Math.min(a.id, b.id)}:${Math.max(a.id, b.id)}`;
}
/** Rails grown from different pieces of one line share real length (not just a junction blob). */
function sameLine(a: Rail, b: Rail): boolean {
  if (a === b) return true;
  const ids = new Set(a.segs);
  let shared = 0;
  b.segs.forEach((id, k) => {
    if (ids.has(id)) shared += b.segLens[k]!;
  });
  return shared >= 20 && shared >= 0.15 * Math.min(a.inkLen, b.inkLen);
}

const PAIR_COS = Math.cos((20 * Math.PI) / 180);
const PAIR_COS_CONT = Math.cos((32 * Math.PI) / 180); // continuing a corridor: its new edge may come in at an angle
const PAIR_RATE = Math.tan((15 * Math.PI) / 180); // spacing may drift this fast along the finger (a narrowing trench)

/**
 * Two rails are the edges of one corridor when they run roughly parallel to each
 * other and their spacing drifts only slowly along the window (a line crossing
 * the corridor at a shallow angle closes in much faster).
 */
function parallelPair(a: Eval, b: Eval, step: number, continuing = false): boolean {
  let first = -1,
    last = -1;
  const cos: number[] = [];
  for (let k = 0; k < a.ok.length; k++) if (a.ok[k] && b.ok[k]) {
    if (first < 0) first = k;
    last = k;
    cos.push(Math.abs(a.tx[k]! * b.tx[k]! + a.ty[k]! * b.ty[k]!));
  }
  if (cos.length < 3) return false;
  cos.sort((p, q) => p - q);
  if (cos[cos.length >> 1]! < (continuing ? PAIR_COS_CONT : PAIR_COS)) return false;
  if (continuing) return true;
  const w0 = a.dFirst + b.dFirst,
    w1 = a.dLast + b.dLast;
  const along = Math.max(step, (last - first) * step);
  return Math.abs(w1 - w0) <= Math.max(4, PAIR_RATE * along);
}

/**
 * A side with its join from the previous stretch's side read from the ink: the straight bridge
 * between the two is sampled and each sample put on the line's edge, so a join across a
 * misread or a rail fragment's end follows the curve like the rest of the border.
 */
function bridgedSide(luma: Luma, prevSide: number[], side: number[], outward: 1 | -1, thick: number): number[] {
  if (prevSide.length < 2 || side.length < 4) return side;
  const px = prevSide[prevSide.length - 2]!,
    py = prevSide[prevSide.length - 1]!;
  const qx = side[0]!,
    qy = side[1]!;
  const d = Math.hypot(qx - px, qy - py);
  if (d < 4 || d > 250) return side;
  const bridge = refineSide(luma, [px, py, qx, qy], outward, thick, 0);
  if (bridge.length < 6) return side;
  return [...bridge.slice(2, -2), ...side];
}
/** `side` shifted to begin where `prev` ends, the shift easing out over `run` px along it. */
function continueFrom(prev: number[], side: number[], run: number): number[] {
  if (prev.length < 2 || side.length < 4) return side;
  const dx = prev[prev.length - 2]! - side[0]!,
    dy = prev[prev.length - 1]! - side[1]!;
  if (Math.hypot(dx, dy) < 0.3 || Math.hypot(dx, dy) > run) return side;
  // the side may be one long straight segment: points every quarter of the run carry the easing
  const out: number[] = [];
  let s = 0,
    next = run / 4;
  const put = (x: number, y: number, at: number) => {
    const f = Math.max(0, 1 - at / run);
    out.push(x + dx * f, y + dy * f);
  };
  put(side[0]!, side[1]!, 0);
  for (let i = 2; i < side.length; i += 2) {
    const x0 = side[i - 2]!,
      y0 = side[i - 1]!,
      x1 = side[i]!,
      y1 = side[i + 1]!;
    const len = Math.hypot(x1 - x0, y1 - y0);
    while (next < s + len && next <= run) {
      const t = (next - s) / len;
      put(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, next);
      next += run / 4;
    }
    s += len;
    put(x1, y1, s);
  }
  return out;
}
/**
 * Append a side onto the one before it so the border only runs on: points of the new side that
 * fall behind the old side's end (two readings of one line overlapping by a pixel or two at a
 * seam) are left out, so the seam can never fold back on itself into a spike.
 */
function joinForward(dst: number[], src: number[]): void {
  let start = 0;
  const m = dst.length;
  if (m >= 4 && src.length >= 4) {
    const ex = dst[m - 2]!,
      ey = dst[m - 1]!;
    let k = m - 4;
    while (k > 0 && Math.hypot(ex - dst[k]!, ey - dst[k + 1]!) < 3) k -= 2;
    const tx = ex - dst[k]!,
      ty = ey - dst[k + 1]!;
    const tl = Math.hypot(tx, ty) || 1;
    while (start + 2 < src.length && ((src[start]! - ex) * tx + (src[start + 1]! - ey) * ty) / tl < -0.3) start += 2;
  }
  appendPts(dst, start ? src.slice(start) : src);
}
function appendGeometry(g: Geometry, left: number[], right: number[], mid: number[]): void {
  appendPts(left, g.left);
  appendPts(right, g.right);
  appendPts(mid, g.mid);
}
function appendPts(dst: number[], src: number[]): void {
  for (let i = 0; i < src.length; i += 2) {
    const m = dst.length;
    if (m === 0 || Math.hypot(src[i]! - dst[m - 2]!, src[i + 1]! - dst[m - 1]!) > 0.3) dst.push(src[i]!, src[i + 1]!);
  }
}
function reverseFlat(pts: number[]): void {
  for (let i = 0, j = pts.length - 2; i < j; i += 2, j -= 2) {
    const x = pts[i]!,
      y = pts[i + 1]!;
    pts[i] = pts[j]!;
    pts[i + 1] = pts[j + 1]!;
    pts[j] = x;
    pts[j + 1] = y;
  }
}
/** Midline between two sides that run the same way: their points paired by fraction of length. */
function averageSides(a: number[], b: number[], step: number): number[] {
  const la = polylineLength(a),
    lb = polylineLength(b);
  const n = Math.max(2, Math.ceil(Math.max(la, lb) / step));
  const out: number[] = [];
  for (let i = 0; i <= n; i++) {
    const f = i / n;
    const pa = pointAtLength(a, f * la),
      pb = pointAtLength(b, f * lb);
    out.push((pa.x + pb.x) / 2, (pa.y + pb.y) / 2);
  }
  return out;
}
function pointAtLength(pts: number[], s: number): Vec {
  let acc = 0;
  for (let i = 0; i + 3 < pts.length; i += 2) {
    const l = Math.hypot(pts[i + 2]! - pts[i]!, pts[i + 3]! - pts[i + 1]!);
    if (acc + l >= s) {
      const t = l === 0 ? 0 : (s - acc) / l;
      return { x: pts[i]! + (pts[i + 2]! - pts[i]!) * t, y: pts[i + 1]! + (pts[i + 3]! - pts[i + 1]!) * t };
    }
    acc += l;
  }
  return { x: pts[pts.length - 2]!, y: pts[pts.length - 1]! };
}
/** Drop polygon vertices closer than `minStep` to their predecessor (the last one wraps to the first). */
function dedupeClosed(pts: number[], minStep: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < pts.length; i += 2) {
    const m = out.length;
    if (m === 0 || Math.hypot(pts[i]! - out[m - 2]!, pts[i + 1]! - out[m - 1]!) >= minStep) out.push(pts[i]!, pts[i + 1]!);
  }
  while (out.length >= 6 && Math.hypot(out[0]! - out[out.length - 2]!, out[1]! - out[out.length - 1]!) < minStep) out.splice(out.length - 2, 2);
  return out;
}
/** The point of an open polyline nearest to p: distance, arc length, the segment's first index, and the point. */
function nearestOnPath(pts: number[], p: Vec): { d: number; s: number; k: number; x: number; y: number } {
  let best = { d: Infinity, s: 0, k: 0, x: pts[0] ?? 0, y: pts[1] ?? 0 };
  let acc = 0;
  for (let k = 0; k + 3 < pts.length; k += 2) {
    const ax = pts[k]!,
      ay = pts[k + 1]!,
      bx = pts[k + 2]!,
      by = pts[k + 3]!;
    const dx = bx - ax,
      dy = by - ay;
    const l2 = dx * dx + dy * dy;
    const l = Math.sqrt(l2);
    const t = l2 ? Math.max(0, Math.min(1, ((p.x - ax) * dx + (p.y - ay) * dy) / l2)) : 0;
    const qx = ax + dx * t,
      qy = ay + dy * t;
    const d = Math.hypot(p.x - qx, p.y - qy);
    if (d < best.d) best = { d, s: acc + t * l, k, x: qx, y: qy };
    acc += l;
  }
  return best;
}
/** Free stretch: the finger's own path with the jitter removed. */
export function freeTail(raw: number[], tol: number): number[] {
  if (raw.length < 4) return raw.slice();
  return simplify(chaikin(simplify(raw, tol * 0.45), 2, false), tol * 0.12);
}
/** Final path: rail stretches stay accurate (staircase removed); free stretches are smoothed hard. */
export function cleanPath(pts: number[], tol: number, mode: LiveMode): number[] {
  if (pts.length < 4) return pts.slice();
  if (mode === 'free') return simplify(chaikin(simplify(pts, tol * 0.45), 2, false), tol * 0.15);
  const a = simplify(pts, Math.max(1.5, tol * 0.15));
  const b = chaikin(a, 1, false);
  return simplify(b, Math.max(1.5, tol * 0.12));
}
